import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Loader,
  Skeleton,
  Text,
} from "@mantine/core";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useDeferredValue,
  startTransition,
} from "react";
import useStyles from "./style";
import moment from "moment-timezone";
import Carousel from "react-multi-carousel";
// import "react-multi-carousel/lib/styles.css";
import { useDispatch, useSelector } from "react-redux";
import DateCard from "../../../../components/Date/DateCard";
import PRNMedicationModal from "../../../../components/PRNMedicationModal";
import TodayDateCard from "../../../../components/TodayDateCard";
import { getMar, getCalendarViewData } from "../../../../services";
import { clearMarCache } from "../../../../services/mar";
import { setMar, setCalendarEntries } from "../../../../store/reducers/mar";
import ScheduleCard from "../ScheduleCard";
import UserDetail from "./UserDetail";
import MarListTab from "./MarListTab";
import Cuations from "../Cuations";
import HideClient from "../ClientHide";
import ClientColumn from "../ClientColumn";
import MonthWeekCard from "../../../../components/MonthWeekCard";
import MonthCard from "../../../../components/MonthCard";
import { useDisclosure } from "@mantine/hooks";
import AddMarReportModal from "../AddMarReportModal";
import { getAllPatients } from "../../../../services/patient";
import { MonthPickerInput } from "@mantine/dates";
import MarMedicationModal from "../../../../components/MarDetailModal/markMedication";
import AddMedicationButton from "../../../../components/AddMedicationButton/AddMedicationButton";
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import { LuPill } from "react-icons/lu";
import {
  firstLetterCap,
  getSpecificMedicationStatus,
  getActiveSlotForDate,
  decrypt,
} from "../../../../utils";
import { getColor } from "..";
import MedicationModal from "../../../../components/MedicationModal";
import MARMedInfoPanel from "../../../../components/MARMedInfoPanel";

const normalizeSlot = (slot) =>
  slot !== null && typeof slot === "object" ? slot.time : slot;

function roundTimeToNearestHalfHour(timeString) {
  const time = moment(timeString, "hh:mm A");
  const minutes = time.minutes();
  let roundedMinutes;
  if (minutes < 15) {
    roundedMinutes = 0;
  } else if (minutes < 45) {
    roundedMinutes = 30;
  } else {
    roundedMinutes = 0;
    time.add(1, "hours");
  }
  time.minutes(roundedMinutes);
  return time.format("hh:mm A")?.toUpperCase();
}

const TIME_SLOTS = [
  {
    title: "12AM - 3AM",
    startTime: "0",
    endTime: "3",
    slots: [
      "12:00 AM",
      "12:30 AM",
      "01:00 AM",
      "01:30 AM",
      "02:00 AM",
      "02:30 AM",
    ],
  },
  {
    title: "3AM - 6AM",
    startTime: "3",
    endTime: "6",
    slots: [
      "03:00 AM",
      "03:30 AM",
      "04:00 AM",
      "04:30 AM",
      "05:00 AM",
      "05:30 AM",
    ],
  },
  {
    title: "6AM - 9AM",
    startTime: "6",
    endTime: "9",
    slots: [
      "06:00 AM",
      "06:30 AM",
      "07:00 AM",
      "07:30 AM",
      "08:00 AM",
      "08:30 AM",
    ],
  },
  {
    title: "9AM - 12PM",
    startTime: "9",
    endTime: "12",
    slots: [
      "09:00 AM",
      "09:30 AM",
      "10:00 AM",
      "10:30 AM",
      "11:00 AM",
      "11:30 AM",
    ],
  },
  {
    title: "12PM - 3PM",
    startTime: "12",
    endTime: "15",
    slots: [
      "12:00 PM",
      "12:30 PM",
      "01:00 PM",
      "01:30 PM",
      "02:00 PM",
      "02:30 PM",
    ],
  },
  {
    title: "3PM - 6PM",
    startTime: "15",
    endTime: "18",
    slots: [
      "03:00 PM",
      "03:30 PM",
      "04:00 PM",
      "04:30 PM",
      "05:00 PM",
      "05:30 PM",
    ],
  },
  {
    title: "6PM - 9PM",
    startTime: "18",
    endTime: "21",
    slots: [
      "06:00 PM",
      "06:30 PM",
      "07:00 PM",
      "07:30 PM",
      "08:00 PM",
      "08:30 PM",
    ],
  },
  {
    title: "9PM - 12AM",
    startTime: "21",
    endTime: "0",
    slots: [
      "09:00 PM",
      "09:30 PM",
      "10:00 PM",
      "10:30 PM",
      "11:00 PM",
      "11:30 PM",
    ],
  },
];

const WEEK_DAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// Convert a flat backend calendar entry (from /mar/getCalendarView) to the
// shape expected by ScheduleCard, MonthCard, MedicationModal, and MarMedicationModal.
// All child components read data.form3.* for medication metadata, data.medicationType[]
// for status, data.client for delete ops, and top-level fields for counts/staff.
const ADMINISTERED_STATUSES = new Set([
  "Taken",
  "Refused",
  "Out of Medication",
  "Unable to Take",
]);

const adaptEntryToCard = (entry, selectedClient) => {
  const form3 = {
    id: entry.medicationId,
    medicationName: entry.medicationName,
    ingestionMode: entry.ingestionMode,
    isNarcotic:
      entry.isNarcotic === true
        ? "Yes"
        : entry.isNarcotic === "Yes"
          ? "Yes"
          : "No",
    frequency: entry.frequency,
    dosage: entry.prescribedDosage,
    unit: entry.dosageUnit,
    dosageForm: entry.dosageForm,
    medicationType: entry.isPRN ? "PRN" : "Schedule",
    medicationUse: entry.medicationUse || null,
    medicationInstruction: entry.medicationInstruction || null,
    medicationadministered: entry.medicationAdministered || null,
    medicineRemaining: entry.medicineRemaining,
  };

  // Whether this entry was actually administered (Taken/Refused/etc.).
  // Only administered entries should pre-fill Administration Date/Time in MedicationModal.
  const wasAdministered =
    ADMINISTERED_STATUSES.has(entry.status) || entry.isPRN;

  // backend staff: { _id, name } object — pass through directly so resolvedStaff
  // in MedicationModal can read .id / ._id / .name without an extra nesting layer.
  const staffObj = entry.staff
    ? { id: entry.staff._id, _id: entry.staff._id, name: entry.staff.name }
    : null;

  // MedicationModal resolves staff and administeredAt from medicationType[].
  // Embed both in the array entry so the existing resolver logic finds them.
  const medicationTypeEntry = entry.isPRN
    ? {
        status: "PRN",
        date: `${entry.date}T12:00:00.000Z`,
        timeSlot: entry.timeSlot,
        dosage: entry.dosage ?? null,
        administeredAt: entry.administeredAt || null,
        staff: staffObj,
      }
    : {
        status: entry.status,
        // Must use the scheduled date (noon UTC) — never administeredAt.
        // getSpecificMedicationStatus() and appointmentsByDate key-match on this date.
        // Using administeredAt here causes pills to disappear on screen.
        date: `${entry.date}T12:00:00.000Z`,
        timeSlot: entry.timeSlot,
        dosage: entry.dosage ?? null,
        administeredAt: wasAdministered ? entry.administeredAt || null : null,
        staff: wasAdministered ? staffObj : null,
      };

  return {
    _id: entry._id,
    medicationId: entry.medicationId,
    date: `${entry.date}T12:00:00.000Z`,
    timeSlot: entry.timeSlot,
    marType: entry.marType,
    form3,
    medicationType: [medicationTypeEntry],
    isUnlimited: entry.isUnlimited,
    medicineRemaining: entry.medicineRemaining,
    totalMedicine: entry.totalMedicine,
    staff: staffObj,
    comments: entry.comments || null,
    reason: entry.reason || null,
    otherReason: entry.otherReason || null,
    dosage: entry.dosage ?? null,
    administeredAt: wasAdministered ? entry.administeredAt || null : null,
    // createdAt: only set for administered entries so MedicationModal doesn't
    // pre-fill Administration Date/Time for upcoming or missed slots.
    createdAt: wasAdministered
      ? entry.administeredAt || `${entry.date}T12:00:00.000Z`
      : null,
    client: selectedClient
      ? { _id: selectedClient.id, id: selectedClient.id }
      : null,
  };
};

const MarTab = ({ userId, view, activeTab }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const dateFormat = "MM-DD-YYYY";

  const prnRef = useRef();
  const [date, setDate] = useState(new Date());
  const [month, setMonth] = useState(new Date(moment().startOf("month")));
  const [prnShow, setPRNShow] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [calendar, setCalendar] = useState(
    localStorage.getItem("calendar_view") || "monthly",
  ); // monthly - weekly - daily

  const [show, setShow] = useState(true);
  const [marLabelCollapsed, setMarLabelCollapsed] = useState(false);
  const [activeMedInfo, setActiveMedInfo] = useState(null); // { row, slots }
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const {
    mar: rawAppointmentsLive,
    calendarEntries: calendarEntriesLive,
    loading: marLoading,
    selectedClient,
  } = useSelector((state) => state.mar);
  console.log("🚀 ~ MarTab ~ selectedClient:", selectedClient);
  const rawAppointments = useDeferredValue(rawAppointmentsLive);
  // Do NOT defer calendarEntries — adaptation is O(n) trivial object construction.
  // Deferring it lets React postpone the update for many seconds when other renders
  // are queued, causing the 15-17s blank calendar observed after the API responds.
  const calendarEntries = calendarEntriesLive;
  // Use mar.loading (set by getCalendarViewData) rather than patient.loading.
  // patient.loading stays true while ALL patient data loads (can be 15-17s),
  // hiding the calendar long after calendarEntries is already populated.
  const loading = marLoading;
  const { profile, timeZone } = useSelector((state) => state.profile);
  const [localSelectedClient, setLocalSelectedClient] = useState();
  const [newCurrentWeek, setNewCurrentWeek] = useState([]);

  // Fast path: adapt backend entries immediately — no heavy computation.
  // This memo has minimal deps so it re-runs only when backend data changes,
  // never when calendar view state (date/month/week) changes.
  const appointmentsFromBackend = useMemo(
    () =>
      calendarEntries?.length > 0
        ? calendarEntries.map((e) => adaptEntryToCard(e, selectedClient))
        : null,
    [calendarEntries, selectedClient],
  );

  // Heavy legacy path: generate virtual entries client-side from raw MAR docs.
  // Only runs when appointmentsFromBackend is null (backend not yet ready or cleared).
  // When appointmentsFromBackend is populated this memo is still memoized but its
  // result is never read — so its expensive computation does NOT block rendering.
  const appointments = useMemo(() => {
    if (appointmentsFromBackend !== null) return appointmentsFromBackend;

    // ── Legacy fallback: generate virtual entries from raw MAR docs ──────────
    // Get medication definitions from localStorage (full unfiltered list).
    // Fall back to Redux selectedClient when localStorage is null — this covers
    // the case where getAllPatientsModal() resolves and dispatches the client to
    // Redux (triggering a memo re-run) but does NOT update localStorage, which
    // happens after navigating away from this page (cleanup removes the key).
    let medications = [];
    try {
      const storedClient =
        JSON.parse(localStorage.getItem("selectedClient")) || selectedClient;
      medications = storedClient?.form3 || [];
    } catch (e) {
      medications = selectedClient?.form3 || [];
    }

    // Helper: given a MAR timeSlot object array and a target moment, return
    // only the time strings whose effectiveFrom..effectiveTo window covers that date.
    const getActiveSlotsForDate = (timeSlotArr, dateObj) => {
      if (!Array.isArray(timeSlotArr)) return [];
      return timeSlotArr
        .filter((slot) => {
          if (!slot?.time) return false;
          const from = slot.effectiveFrom
            ? moment(slot.effectiveFrom).startOf("day")
            : null;
          const to = slot.effectiveTo
            ? moment(slot.effectiveTo).startOf("day")
            : null;
          if (from && from.isAfter(dateObj, "day")) return false;
          if (to && to.isBefore(dateObj, "day")) return false;
          return true;
        })
        .map((slot) => slot.time);
    };

    // Always merge medications from rawAppointments that are not already in the
    // localStorage list. This handles the case where a new medication was added
    // after the client was selected (localStorage form3 is stale and missing the
    // new medication), so virtual calendar entries are generated for ALL medicines.
    if (rawAppointments?.length) {
      const seen = new Set(medications.map((m) => Number(m.id)));
      rawAppointments.forEach((apt) => {
        if (seen.has(Number(apt.medicationId))) return;
        seen.add(Number(apt.medicationId));

        // Prefer the nested form3 definition if it was populated; otherwise
        // reconstruct a compatible object from the MAR document's own fields.
        const medDef = apt.form3 || {
          id: apt.medicationId,
          timeSlots: Array.isArray(apt.timeSlot)
            ? apt.timeSlot.map(normalizeSlot)
            : [normalizeSlot(apt.timeSlot)].filter(Boolean),
          scheduleType: apt.scheduleType || "daily",
          repeatType: apt.repeatType || null,
          repeatEvery: apt.repeatEvery || 1,
          repeatOn: Array.isArray(apt.repeatOn) ? apt.repeatOn : [],
          medicationDate: apt.medicationDate || apt.date,
          medicationEndDate: apt.medicationEndDate || null,
          medicineRemaining: apt.medicineRemaining,
          totalMedicine: apt.totalMedicine,
          isUnlimited: apt.isUnlimited,
        };
        medications.push(medDef);
      });
    }

    if (!medications.length) return rawAppointments || [];

    // ─── Enrich medications with fresh schedule metadata from MAR documents ───────
    // localStorage form3 is only updated when the client is (re-)selected, so after
    // a schedule edit it stays stale.  The MAR document is always fetched fresh from
    // the server, so its scheduleType / repeatType / repeatEvery / repeatOn /
    // medicationDate / medicationEndDate / timeSlot fields are the authoritative
    // source of truth for how to generate virtual calendar entries.
    //
    // For every medication from localStorage we find its most-recent MAR document
    // and overwrite the schedule-related fields so the virtual-entry loop below
    // always uses the latest saved schedule — not whatever was in localStorage when
    // the client was first opened.
    if (rawAppointments?.length) {
      medications = medications.map((med) => {
        const marDoc = (rawAppointments || [])
          .filter((r) => Number(r.medicationId) === Number(med.id))
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

        if (!marDoc) return med;

        // Build timeSlots from the MAR doc's timeSlot field (always authoritative)
        const freshTimeSlots =
          Array.isArray(marDoc.timeSlot) && marDoc.timeSlot.length > 0
            ? marDoc.timeSlot.map(normalizeSlot)
            : marDoc.timeSlot
              ? [normalizeSlot(marDoc.timeSlot)]
              : null;

        return {
          ...med,
          // Store the full timeSlot object array from the MAR doc so virtual entry
          // generation can resolve active slots per date (effectiveFrom / effectiveTo).
          rawTimeSlot:
            Array.isArray(marDoc.timeSlot) && marDoc.timeSlot.length > 0
              ? marDoc.timeSlot
              : null,
          // If no rawTimeSlot from MAR doc, try to use form3's fallbackTimeSlotObjects or timeSlot
          fallbackTimeSlotObjects:
            Array.isArray(marDoc.timeSlot) && marDoc.timeSlot.length > 0
              ? marDoc.timeSlot
              : med.fallbackTimeSlotObjects || med.timeSlot || [],
          ...(marDoc.scheduleType !== undefined &&
            marDoc.scheduleType !== null && {
              scheduleType: marDoc.scheduleType,
            }),
          ...(marDoc.repeatType !== undefined && {
            repeatType: marDoc.repeatType,
          }),
          ...(marDoc.repeatEvery !== undefined &&
            marDoc.repeatEvery !== null && {
              repeatEvery: marDoc.repeatEvery,
            }),
          ...(Array.isArray(marDoc.repeatOn) && {
            repeatOn: marDoc.repeatOn,
          }),
          ...(marDoc.medicationDate && {
            medicationDate: marDoc.medicationDate,
          }),
          // null medicationEndDate is intentional (no end date) — keep it
          ...(marDoc.medicationEndDate !== undefined && {
            medicationEndDate: marDoc.medicationEndDate,
          }),
          // Carry schedule version history so the virtual-entry loop can generate
          // correct entries for past date ranges that used a different schedule.
          scheduleVersions:
            marDoc.scheduleVersions || med.scheduleVersions || [],
          scheduleEffectiveFrom:
            marDoc.scheduleEffectiveFrom || med.scheduleEffectiveFrom || null,
          medicineCountVersions:
            marDoc.medicineCountVersions || med.medicineCountVersions || [],
          medicineCountEffectiveFrom:
            marDoc.medicineCountEffectiveFrom ||
            med.medicineCountEffectiveFrom ||
            null,
        };
      });
    }
    // ─────────────────────────────────────────────────────────────────────────────

    // Determine view date range based on calendar type
    let viewStart, viewEnd;
    if (calendar === "monthly") {
      const cm = moment(month).clone().startOf("month");
      const prevEnd = cm.clone().subtract(1, "month").endOf("month");
      const lastSun = prevEnd.clone().endOf("week").subtract(6, "days");
      const diff = cm.diff(lastSun, "days") >= 6;
      viewStart = (diff ? cm : lastSun).clone().startOf("day");
      viewEnd = viewStart.clone().add(34, "days").endOf("day");
    } else if (calendar === "weekly" && newCurrentWeek?.length >= 7) {
      viewStart = moment(newCurrentWeek[0]).startOf("day");
      viewEnd = moment(newCurrentWeek[6]).endOf("day");
    } else if (calendar === "daily") {
      viewStart = moment(date).startOf("day");
      viewEnd = moment(date).endOf("day");
    } else {
      return rawAppointments || [];
    }

    // Build set of existing record keys to avoid duplicates
    const existingKeys = new Set();
    (rawAppointments || []).forEach((r) => {
      const dateStr = moment(r.date).utc().format("YYYY-MM-DD");
      const slots = Array.isArray(r.timeSlot)
        ? r.timeSlot.map((s) => s?.time).filter(Boolean)
        : [];
      slots.forEach((slot) => {
        existingKeys.add(`${dateStr}_${normalizeSlot(slot)}_${r.medicationId}`);
      });
    });

    const virtualEntries = [];
    let generatedVirtualCount = 0;
    let skippedVirtualCount = 0;

    // Returns an ordered array of schedule periods for a medication.
    // Historical periods come from med.scheduleVersions; the current period
    // covers from med.scheduleEffectiveFrom (or medStart) onwards.
    // When scheduleVersions is empty (no prior edits) a single period covering
    // the full medication range is returned, preserving existing behaviour.
    const buildSchedulePeriods = (med, medStart) => {
      const periods = [];
      if (
        Array.isArray(med.scheduleVersions) &&
        med.scheduleVersions.length > 0
      ) {
        med.scheduleVersions.forEach((version) => {
          if (!version.effectiveFrom) return;
          const anchor = moment(version.effectiveFrom).startOf("day");
          periods.push({
            from: anchor.clone(),
            to: version.effectiveTo
              ? moment(version.effectiveTo).endOf("day")
              : null,
            scheduleType: version.scheduleType || "daily",
            repeatType: version.repeatType || null,
            repeatEvery: Number(version.repeatEvery) || 1,
            repeatOn: Array.isArray(version.repeatOn) ? version.repeatOn : [],
            anchor,
          });
        });
      }
      // Current active period — anchored to scheduleEffectiveFrom (when the latest
      // schedule change took effect) or to medStart if the schedule has never changed.
      const currentFrom = med.scheduleEffectiveFrom
        ? moment(med.scheduleEffectiveFrom).startOf("day")
        : medStart.clone();

      // For custom repeat-by-day schedules (e.g. every-other-day), the phase origin
      // must be the original medication start date (medStart), not scheduleEffectiveFrom.
      // scheduleEffectiveFrom marks when settings changed, not when the cycle started.
      // Using it as the phase anchor shifts the sequence by 1 day relative to existing
      // MAR records, producing phantom "Missed" entries on off-days (CS-28).
      const schedType = med.scheduleType || "daily";
      const repType = (med.repeatType || "day").toLowerCase();
      const repEvery = Number(med.repeatEvery) || 1;
      const phaseAnchor =
        schedType === "custom" && repType === "day" && repEvery > 1
          ? medStart.clone()
          : currentFrom.clone();

      periods.push({
        from: currentFrom,
        to: null,
        scheduleType: schedType,
        repeatType: med.repeatType || null,
        repeatEvery: repEvery,
        repeatOn: Array.isArray(med.repeatOn) ? med.repeatOn : [],
        anchor: phaseAnchor,
      });
      return periods;
    };

    medications.forEach((med) => {
      // Skip PRN medications entirely.
      // Discontinued medications are allowed through so past "Missed" virtual
      // entries are still generated for them — their medEnd is capped at
      // yesterday below so no future "upcoming" slots appear.
      const medStatus = Array.isArray(med.medicationType)
        ? med.medicationType[med.medicationType.length - 1]?.status
        : med.medicationType;
      if (medStatus === "PRN") return;

      const isDiscontinued = med.discontinued || medStatus === "discontinued";

      const medStart = moment(med.medicationDate).startOf("day");
      // When no end date is set the medication repeats indefinitely — use
      // viewEnd as the cap so we generate entries for the current view window.
      // Using medStart.endOf('day') as the fallback (the old behaviour) meant
      // iterEnd was always the start day and weekly/monthly repeats never showed
      // any entries beyond day 1.
      const isSentinelEndDate = (endDate) => {
        if (!endDate) return false;
        const d = new Date(endDate);
        if (isNaN(d.getTime())) return false;
        const isDecember31UTC = d.getUTCMonth() === 11 && d.getUTCDate() === 31;
        const isEndOfDaySeconds =
          d.getUTCSeconds() === 59 && d.getUTCMilliseconds() === 999;
        const isJan1UTC = d.getUTCMonth() === 0 && d.getUTCDate() === 1;
        const isEndOfPrevYearLocal =
          isJan1UTC && d.getUTCHours() < 5 && isEndOfDaySeconds;
        return (isDecember31UTC && isEndOfDaySeconds) || isEndOfPrevYearLocal;
      };
      let medEnd =
        med.medicationEndDate &&
        med.medicationEndDate !== "null" &&
        !isSentinelEndDate(med.medicationEndDate)
          ? moment(med.medicationEndDate).startOf("day").endOf("day")
          : viewEnd.clone();

      // For discontinued medications, cap medEnd so slots on/after the
      // discontinuation date are hidden:
      //  - If discontinuedOn is in the past  → cap at (discontinuedOn - 1 day)
      //    so slots from that date onwards are hidden.
      //  - If discontinuedOn is today or future → cap at yesterday, because we
      //    never want upcoming slots shown for a discontinued medicine.
      // Effective rule: cap = min(discontinuedOn - 1 day, yesterday)
      if (isDiscontinued) {
        const endOfYesterday = moment().subtract(1, "day").endOf("day");
        let discontinuedCap = endOfYesterday;
        if (med.discontinuedOn) {
          const dayBeforeDiscontinued = moment(med.discontinuedOn)
            .subtract(1, "day")
            .endOf("day");
          if (dayBeforeDiscontinued.isBefore(endOfYesterday)) {
            discontinuedCap = dayBeforeDiscontinued;
          }
        }
        if (medEnd.isAfter(discontinuedCap)) medEnd = discontinuedCap;
      }

      // Skip if medication range doesn't overlap with view range
      if (medEnd.isBefore(viewStart) || medStart.isAfter(viewEnd)) return;

      const medId = med.id;

      const iterStart = moment.max(viewStart.clone(), medStart.clone());
      const iterEnd = moment.min(viewEnd.clone(), medEnd.clone());

      // Find the most-recent MAR record for this medication to use as template.
      // Using the latest record ensures refill counts (totalMedicine/medicineRemaining)
      // are reflected rather than an arbitrary older record with stale values.
      const sampleRecord = (rawAppointments || [])
        .filter((r) => Number(r.medicationId) === Number(medId))
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

      // Most-recent MAR record strictly in the past (date < today).
      // editmedication only updates today-and-future records, so past records keep
      // their pre-edit medicineRemaining / totalMedicine values.  Using this as the
      // base for past virtual entries means a refill or unlimited toggle does NOT
      // retroactively change how historical missed/upcoming slots are displayed.
      const _startOfToday = moment().startOf("day");
      const pastSampleRecord = (rawAppointments || [])
        .filter(
          (r) =>
            Number(r.medicationId) === Number(medId) &&
            moment(r.date).isBefore(_startOfToday),
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

      // Collect all Taken entries (date + time + dosage) for this medication.
      // medicineRemaining is computed dynamically on the frontend as:
      //   base - sum(dosages of all Taken entries at or before this slot's date+time)
      const allTakenForMed = [];
      (rawAppointments || [])
        .filter((r) => Number(r.medicationId) === Number(medId))
        .forEach((r) => {
          if (Array.isArray(r.medicationType)) {
            r.medicationType.forEach((entry) => {
              if (entry.status === "Taken") {
                allTakenForMed.push({
                  dateStr: moment(entry.date || entry.administeredAt).format(
                    "YYYY-MM-DD",
                  ),
                  timeSlot: entry.timeSlot || "",
                  dosage: Number(entry.dosage || 0),
                });
              }
            });
          }
        });

      const generateEntry = (dateObj, slot) => {
        const dateStr = dateObj.format("YYYY-MM-DD");
        const key = `${dateStr}_${slot}_${medId}`;

        // If the key exists because the real MAR record's `date` field is this day
        // (the medication start date), AND that record has medicationType:[] (empty),
        // AND the date is in the past — we still generate a 'Missed' virtual entry.
        // Otherwise, we still generate virtual entries so dedupe step can overlay status.
        if (existingKeys.has(key)) {
          const realRecord = (rawAppointments || []).find((r) => {
            if (Number(r.medicationId) !== Number(medId)) return false;
            const rDateStr = moment(r.date).format("YYYY-MM-DD");
            if (rDateStr !== dateStr) return false;
            const rSlots = Array.isArray(r.timeSlot)
              ? r.timeSlot.map(normalizeSlot)
              : [normalizeSlot(r.timeSlot)];
            return rSlots.includes(slot);
          });

          const hasEmptyMedicationType =
            realRecord &&
            Array.isArray(realRecord.medicationType) &&
            realRecord.medicationType.length === 0;
          const isPast = dateObj.isBefore(moment().startOf("day"));

          if (hasEmptyMedicationType && isPast) {
            // keep generating so we show Missed on past empty records
          }
          // keep generating for all existing keys; we will overlay corrected status later
        } else {
          existingKeys.add(key);
        }

        // Check if any existing record for this med+slot has a medicationType entry for this date
        const existingForSlot = (rawAppointments || []).find(
          (r) =>
            Number(r.medicationId) === Number(medId) &&
            (Array.isArray(r.timeSlot)
              ? r.timeSlot.map(normalizeSlot).includes(slot)
              : normalizeSlot(r.timeSlot) === slot) &&
            Array.isArray(r.medicationType),
        );

        const _nowInBiz = moment();
        const _startOfToday = _nowInBiz.clone().startOf("day");
        let entryStatus;
        if (dateObj.isAfter(_startOfToday, "day")) {
          entryStatus = "upcoming";
        } else if (dateObj.isSame(_startOfToday, "day")) {
          // Today: check whether the slot time has already passed
          const _slotMoment = slot ? moment(slot, ["hh:mm A", "HH:mm"]) : null;
          if (_slotMoment && _slotMoment.isValid()) {
            const _slotToday = _startOfToday.clone().set({
              hour: _slotMoment.hours(),
              minute: _slotMoment.minutes(),
              second: 0,
              millisecond: 0,
            });
            entryStatus = _nowInBiz.isSameOrAfter(_slotToday)
              ? "Missed"
              : "upcoming";
          } else {
            entryStatus = "upcoming";
          }
        } else {
          entryStatus = "Missed";
        }
        let entryId = `virtual_${key}`;
        let staffFromEntry = null;
        let commentsFromEntry = null;
        // Per-entry historical values migrated from old schema.
        // null = no administered record for this date (Missed/upcoming).
        let dosageFromEntry = null;
        let medicineRemainingFromEntry = null;
        // The actual administered datetime for this entry (from medicationType[n].date).
        // Used as createdAt on the virtual entry so MedicationModal shows the correct
        // date/time instead of falling back to moment(undefined) = current time.
        let createdAtFromEntry = null;
        let administeredAtFromEntry = null;

        if (existingForSlot) {
          // Check if this record's array has an entry matching the virtual date
          const matching = existingForSlot.medicationType.filter(
            (m) =>
              moment(m.date).format("YYYY-MM-DD") === dateStr &&
              (!m.timeSlot || m.timeSlot === slot),
          );
          if (matching.length > 0) {
            const lastMatch = matching[matching.length - 1];
            entryStatus = lastMatch.status;
            staffFromEntry = lastMatch?.staff || null;
            commentsFromEntry = lastMatch?.comments || null;
            // Extract per-entry historical values stored by the migration.
            // These are null on virtual (Missed/upcoming) entries — only present
            // when the medication was actually administered on this date.
            dosageFromEntry = lastMatch?.dosage ?? null;
            // medicineRemaining lives at the top level of the DB record, not inside
            // the medicationType array entry. Read it from lastMatch first (future-proof),
            // then fall back to the top-level existingForSlot value.
            medicineRemainingFromEntry =
              lastMatch?.medicineRemaining ??
              existingForSlot?.medicineRemaining ??
              null;
            administeredAtFromEntry = lastMatch?.administeredAt || null;
            // Use the entry's own date as createdAt so MedicationModal shows the
            // administered date/time correctly.
            // Pre-fix records stored only midnight UTC in medicationType[n].date.
            // In that case fall back to the parent document's createdAt, which
            // holds the actual clock time for single-entry documents (production
            // data recorded before the fix). This is imperfect for multi-entry
            // documents (shows the latest entry's time), but far better than midnight.
            const entryDateObj = lastMatch?.date
              ? new Date(lastMatch.date)
              : null;
            const entryIsMidnight =
              entryDateObj &&
              entryDateObj.getUTCHours() === 0 &&
              entryDateObj.getUTCMinutes() === 0 &&
              entryDateObj.getUTCSeconds() === 0 &&
              entryDateObj.getUTCMilliseconds() === 0;
            createdAtFromEntry =
              entryDateObj && !entryIsMidnight
                ? lastMatch.date
                : existingForSlot?.createdAt || lastMatch?.date || null;
          }
          // Always use the real record ID so saves update the existing record
          entryId = existingForSlot._id;
        }

        generatedVirtualCount++;
        virtualEntries.push({
          _id: entryId,
          date: `${dateStr}T12:00:00.000Z`,
          timeSlot: slot,
          medicationId: medId,
          medicationType: [
            {
              status: entryStatus,
              date: `${dateStr}T12:00:00.000Z`,
              administeredAt: administeredAtFromEntry,
              dosage: dosageFromEntry,
            },
          ],
          form3: med,
          // isUnlimited-aware display logic:
          // - If the medication is currently unlimited (med.medicineRemaining === 'unlimited'
          //   or sampleRecord?.isUnlimited is true):
          //     * Today and future dates → show 'unlimited'
          //     * Past dates → show the stored numeric count from the MAR record
          //       (sampleRecord holds the most-recent real DB record for this med)
          // - For historical versions (medicineCountVersions): use that version's
          //   stored base and cap addBack at the version's effectiveTo boundary.
          medicineRemaining: (() => {
            // Resolve which medicine-count version covers this entry's date.
            const _countVersions = Array.isArray(med.medicineCountVersions)
              ? med.medicineCountVersions
              : [];
            const _matchedCountV =
              _countVersions.find((v) => {
                if (!v.effectiveFrom) return false;
                const vFrom = moment(v.effectiveFrom).startOf("day");
                const vTo = v.effectiveTo
                  ? moment(v.effectiveTo).endOf("day")
                  : null;
                return (
                  dateObj.isSameOrAfter(vFrom) &&
                  (vTo === null || dateObj.isSameOrBefore(vTo))
                );
              }) || null;

            const _vIsUnlimited = _matchedCountV
              ? !!_matchedCountV.isUnlimited
              : med.medicineRemaining === "unlimited" ||
                sampleRecord?.isUnlimited === true;

            if (_vIsUnlimited) {
              if (_matchedCountV) {
                // Historical period was unlimited — return stored value
                return _matchedCountV.medicineRemaining ?? "unlimited";
              }
              // Current version is unlimited
              const isPastDate = dateObj.isBefore(moment().startOf("day"));
              if (isPastDate) {
                return (
                  sampleRecord?.medicineRemaining ??
                  med.medicineRemaining ??
                  "unlimited"
                );
              }
              return "unlimited";
            }

            // Numeric — base from the matched version or current top-level value.
            const baseRaw = _matchedCountV
              ? _matchedCountV.medicineRemaining
              : (med.medicineRemaining ??
                sampleRecord?.medicineRemaining ??
                null);
            if (baseRaw === null || baseRaw === undefined) return null;
            if (isNaN(Number(baseRaw))) return baseRaw;

            const dateStr = dateObj.format("YYYY-MM-DD");
            const slotMins = (() => {
              const m = moment(slot, ["hh:mm A", "HH:mm"]);
              return m.isValid() ? m.hours() * 60 + m.minutes() : 0;
            })();

            if (_matchedCountV) {
              // ── Historical version ─────────────────────────────────────────────
              // baseRaw is the count that was archived at refill time (i.e. the
              // initial count for this period — doses may have been marked AFTER
              // the refill, so they are not baked into this value).
              // Strategy: subtract ALL within-version doses to get the true
              // end-of-period count, then add back doses that fall AFTER this slot
              // (restoring them so the slot reflects what was remaining at that
              // specific point in time).
              const versionStartStr = moment(
                _matchedCountV.effectiveFrom,
              ).format("YYYY-MM-DD");
              const versionEndStr = _matchedCountV.effectiveTo
                ? moment(_matchedCountV.effectiveTo).format("YYYY-MM-DD")
                : null;

              const totalDosesInVersion = allTakenForMed.reduce((sum, e) => {
                if (e.dateStr < versionStartStr) return sum;
                if (versionEndStr && e.dateStr > versionEndStr) return sum;
                return sum + e.dosage;
              }, 0);

              const effectiveBase = Number(baseRaw) - totalDosesInVersion;

              const addBack = allTakenForMed.reduce((sum, e) => {
                if (e.dateStr < versionStartStr) return sum;
                if (versionEndStr && e.dateStr > versionEndStr) return sum;
                if (e.dateStr > dateStr) return sum + e.dosage;
                if (e.dateStr === dateStr) {
                  const eMins = (() => {
                    const m = moment(e.timeSlot, ["hh:mm A", "HH:mm"]);
                    return m.isValid() ? m.hours() * 60 + m.minutes() : 0;
                  })();
                  if (eMins > slotMins) return sum + e.dosage;
                }
                return sum;
              }, 0);

              return String(effectiveBase + addBack);
            }

            // ── Current version ────────────────────────────────────────────────
            // baseRaw comes from form3.medicineRemaining which the backend
            // decrements in real-time when doses are taken (Patient.findByIdAndUpdate).
            // It already reflects all doses ever marked — including doses from the
            // OLD period that were belatedly marked after the refill.
            // Strategy: add back doses taken AFTER this slot, but only doses
            // belonging to the current version period (exclude old-version doses
            // which are already baked into baseRaw via the backend decrement).
            const currentVersionStartStr = med.medicineCountEffectiveFrom
              ? moment(med.medicineCountEffectiveFrom).format("YYYY-MM-DD")
              : null;
            const addBack = allTakenForMed.reduce((sum, e) => {
              if (currentVersionStartStr && e.dateStr < currentVersionStartStr)
                return sum;
              if (e.dateStr > dateStr) return sum + e.dosage;
              if (e.dateStr === dateStr) {
                const eMins = (() => {
                  const m = moment(e.timeSlot, ["hh:mm A", "HH:mm"]);
                  return m.isValid() ? m.hours() * 60 + m.minutes() : 0;
                })();
                if (eMins > slotMins) return sum + e.dosage;
              }
              return sum;
            }, 0);
            return String(Number(baseRaw) + addBack);
          })(),
          totalMedicine: (() => {
            const _countVersions = Array.isArray(med.medicineCountVersions)
              ? med.medicineCountVersions
              : [];
            const _matchedCountV =
              _countVersions.find((v) => {
                if (!v.effectiveFrom) return false;
                const vFrom = moment(v.effectiveFrom).startOf("day");
                const vTo = v.effectiveTo
                  ? moment(v.effectiveTo).endOf("day")
                  : null;
                return (
                  dateObj.isSameOrAfter(vFrom) &&
                  (vTo === null || dateObj.isSameOrBefore(vTo))
                );
              }) || null;

            if (_matchedCountV) {
              return _matchedCountV.isUnlimited
                ? "unlimited"
                : (_matchedCountV.totalMedicine ?? med.totalMedicine ?? null);
            }
            const isUnlimitedMed =
              med.totalMedicine === "unlimited" ||
              sampleRecord?.isUnlimited === true;
            if (isUnlimitedMed) {
              const isPastDate = dateObj.isBefore(moment().startOf("day"));
              if (isPastDate) {
                return (
                  sampleRecord?.totalMedicine ??
                  med.totalMedicine ??
                  "unlimited"
                );
              }
              return "unlimited";
            }
            return (
              med.totalMedicine ?? sampleRecord?.totalMedicine ?? "unlimited"
            );
          })(),
          isUnlimited: (() => {
            const _countVersions = Array.isArray(med.medicineCountVersions)
              ? med.medicineCountVersions
              : [];
            const _matchedCountV =
              _countVersions.find((v) => {
                if (!v.effectiveFrom) return false;
                const vFrom = moment(v.effectiveFrom).startOf("day");
                const vTo = v.effectiveTo
                  ? moment(v.effectiveTo).endOf("day")
                  : null;
                return (
                  dateObj.isSameOrAfter(vFrom) &&
                  (vTo === null || dateObj.isSameOrBefore(vTo))
                );
              }) || null;
            if (_matchedCountV) return !!_matchedCountV.isUnlimited;
            return (
              sampleRecord?.isUnlimited ??
              med.medicineRemaining === "unlimited" ??
              false
            );
          })(),
          client: sampleRecord?.client || existingForSlot?.client || null,
          staff: staffFromEntry || existingForSlot?.staff || null,
          comments: commentsFromEntry || existingForSlot?.comments || null,
        });
      };

      // Resolve active slots for a given date from rawTimeSlot objects.
      // Falls back to form3.timeSlots (strings) when no MAR doc slot history exists.
      const getSlotsForDate = (dateObj) => {
        if (med.rawTimeSlot)
          return getActiveSlotsForDate(med.rawTimeSlot, dateObj);
        // Try fallback timeSlot objects which may have effectiveFrom/effectiveTo dates
        if (
          Array.isArray(med.fallbackTimeSlotObjects) &&
          med.fallbackTimeSlotObjects.length > 0
        ) {
          // Check if fallback objects have effectiveFrom/effectiveTo properties
          const hasDateInfo = med.fallbackTimeSlotObjects.some(
            (s) => s?.effectiveFrom || s?.effectiveTo,
          );
          if (hasDateInfo) {
            return getActiveSlotsForDate(med.fallbackTimeSlotObjects, dateObj);
          }
        }
        return Array.isArray(med.timeSlots) ? med.timeSlots : [];
      };

      // ─── Multi-period virtual entry generation ───────────────────────────────
      // Each period in schedulePeriods covers [from, to] and has its own schedule
      // fields. For medications with no prior edits, buildSchedulePeriods returns
      // one period spanning the full range — identical to the old single-schedule
      // behaviour.  For edited medications, one period per historical version plus
      // one open-ended period for the current schedule are returned.
      const _dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const schedulePeriods = buildSchedulePeriods(med, medStart);

      schedulePeriods.forEach((period) => {
        const periodIterStart = moment.max(
          period.from.clone(),
          iterStart.clone(),
        );
        const periodIterEnd = period.to
          ? moment.min(period.to.clone(), iterEnd.clone())
          : iterEnd.clone();

        if (periodIterStart.isAfter(periodIterEnd, "day")) return;

        const pScheduleType = period.scheduleType || "daily";
        const pAnchor = period.anchor;

        if (pScheduleType === "daily") {
          let current = periodIterStart.clone();
          while (current.isSameOrBefore(periodIterEnd, "day")) {
            getSlotsForDate(current).forEach((slot) =>
              generateEntry(current.clone(), slot),
            );
            current.add(1, "day");
          }
        } else if (pScheduleType === "custom") {
          const pRepeatType = (period.repeatType || "day").toLowerCase();
          const pRepeatEvery = Number(period.repeatEvery) || 1;
          const pRepeatOn = Array.isArray(period.repeatOn)
            ? period.repeatOn
            : [];

          if (pRepeatType === "day") {
            let current = pAnchor.clone();
            while (current.isSameOrBefore(periodIterEnd, "day")) {
              if (current.isSameOrAfter(periodIterStart, "day")) {
                getSlotsForDate(current).forEach((slot) =>
                  generateEntry(current.clone(), slot),
                );
              }
              current.add(pRepeatEvery, "day");
            }
          } else if (pRepeatType === "week") {
            const pEffectiveRepeatOn =
              pRepeatOn.length > 0 ? pRepeatOn : [_dayNames[pAnchor.day()]];
            let weekCursor = pAnchor.clone().startOf("week");
            while (weekCursor.isSameOrBefore(periodIterEnd, "day")) {
              pEffectiveRepeatOn.forEach((dayName) => {
                const dayIndex = _dayNames.indexOf(dayName);
                if (dayIndex === -1) return;
                const targetDate = weekCursor.clone().add(dayIndex, "days");
                if (
                  targetDate.isSameOrAfter(periodIterStart, "day") &&
                  targetDate.isSameOrBefore(periodIterEnd, "day") &&
                  targetDate.isSameOrAfter(pAnchor, "day")
                ) {
                  getSlotsForDate(targetDate).forEach((slot) =>
                    generateEntry(targetDate.clone(), slot),
                  );
                }
              });
              weekCursor.add(pRepeatEvery, "week");
            }
          } else if (pRepeatType === "month") {
            let current = pAnchor.clone();
            while (current.isSameOrBefore(periodIterEnd, "day")) {
              if (current.isSameOrAfter(periodIterStart, "day")) {
                getSlotsForDate(current).forEach((slot) =>
                  generateEntry(current.clone(), slot),
                );
              }
              current.add(pRepeatEvery, "month");
            }
          }
        }
      });
      // ─────────────────────────────────────────────────────────────────────────
    });

    // Helper: resolve the true PRN status from medicationType, handling:
    //   - plain string: "PRN"
    //   - array of {status, date}  where status may itself be a stringified array
    //   - double-stringified JSON (how the DB actually stores it)
    const resolveMedTypePRN = (medType) => {
      if (!medType) return false;

      // If it's a plain string — could be "PRN" or a JSON string
      if (typeof medType === "string") {
        if (medType.toUpperCase() === "PRN") return true;
        // Try to parse as JSON
        try {
          const parsed = JSON.parse(medType);
          return resolveMedTypePRN(parsed); // recurse with parsed value
        } catch (e) {
          return false;
        }
      }

      // If it's an array, check the last entry's status (may itself be stringified)
      if (Array.isArray(medType)) {
        if (medType.length === 0) return false;
        const last = medType[medType.length - 1];
        return resolveMedTypePRN(last?.status); // recurse into status
      }

      return false;
    };

    const isRawRecordPRN = (r) => {
      // Check form3 definition first (most reliable source)
      if (r?.form3?.medicationType !== undefined) {
        if (resolveMedTypePRN(r.form3.medicationType)) return true;
      }
      // Fallback: check the record's own medicationType field
      return resolveMedTypePRN(r?.medicationType);
    };

    // Pre-compute per-PRN-medication running medicineRemaining keyed by
    // `${medicationId}_${dateStr}_${slotMins}` so each administration shows
    // its own chronologically-correct count instead of the shared outer value.
    const prnRemainingMap = {};
    (() => {
      const byMedId = {};
      (rawAppointments || []).forEach((r) => {
        if (!isRawRecordPRN(r)) return;
        if (!Array.isArray(r.medicationType)) return;
        const mId = Number(r.medicationId);
        if (!byMedId[mId])
          byMedId[mId] = { entries: [], outer: r.medicineRemaining };
        r.medicationType.forEach((entry) => {
          if (entry?.status !== "PRN") return;
          const em = moment(entry.date);
          if (!em.isValid()) return;
          byMedId[mId].entries.push({
            dateStr: em.format("YYYY-MM-DD"),
            slotMins: em.hours() * 60 + em.minutes(),
            dosage: Number(entry.dosage || 0),
          });
        });
      });
      Object.entries(byMedId).forEach(([mIdStr, { entries, outer }]) => {
        if (outer === "unlimited" || isNaN(Number(outer))) return;
        entries.sort((a, b) =>
          a.dateStr !== b.dateStr
            ? a.dateStr < b.dateStr
              ? -1
              : 1
            : a.slotMins - b.slotMins,
        );
        let running = Number(outer);
        entries.forEach((e) => {
          running -= e.dosage;
          prnRemainingMap[`${mIdStr}_${e.dateStr}_${e.slotMins}`] =
            String(running);
        });
      });
    })();

    // Flatten raw appointments: records with array timeSlot become one entry per slot.
    // PRN records are expanded by their medicationType entries so each administration
    // has its own `date` field — this restores date-filtering in both week and daily
    // PRN sections after switching from create-per-administration to find-or-append.
    const flattenedAppointments = (rawAppointments || []).flatMap((r) => {
      if (isRawRecordPRN(r)) {
        if (Array.isArray(r.medicationType) && r.medicationType.length > 0) {
          return r.medicationType
            .filter((entry) => entry?.status === "PRN")
            .map((entry) => {
              const em = moment(entry.date);
              const dateStr = em.isValid() ? em.format("YYYY-MM-DD") : "";
              const slotMins = em.isValid()
                ? em.hours() * 60 + em.minutes()
                : 0;
              const computedRemaining =
                prnRemainingMap[
                  `${Number(r.medicationId)}_${dateStr}_${slotMins}`
                ];
              return {
                ...r,
                date: entry.date,
                administeredAt: entry.administeredAt || entry.date,
                medicationType: [entry],
                medicineRemaining: computedRemaining ?? r.medicineRemaining,
                staff: entry.staff || r.staff,
                comments: entry.comments || r.comments,
                reason: entry.reason || r.reason,
                otherReason: entry.otherReason || r.otherReason,
              };
            });
        }
        return [r]; // no administered entries yet — keep whole document
      }
      if (!Array.isArray(r.timeSlot)) return [r];
      const activeSlots = getActiveSlotsForDate(
        r.timeSlot,
        moment(r.date).startOf("day"),
      );
      if (activeSlots.length === 0) return [];
      return activeSlots.map((slotTime) => ({ ...r, timeSlot: slotTime }));
    });

    // ─── Historical Taken/Missed Recovery ────────────────────────────────────────
    // When a medication's date range is updated (e.g. Feb 20–Mar 1 → Feb 26–Mar 5),
    // the virtual-entry loop above only iterates the NEW range. Any Taken/Missed
    // entries recorded BEFORE the new start date live in the DB (inside
    // medicationType[]) but are never rendered because those dates are outside the
    // new medicationDate range. Fix: scan every rawAppointment's medicationType array
    // for historical entries within the current VIEW range and inject them.
    const recoveredEntries = [];
    // Tracks keys added during this recovery loop only (used for discontinued-record dedup).
    // Discontinued records must NOT be blocked by the initialization-time existingKeys
    // (which uses document.date + slot), because their historical statuses live in
    // medicationType entries whose dates differ from the document's date.
    const recoveredKeys = new Set();

    (rawAppointments || []).forEach((r) => {
      if (isRawRecordPRN(r)) return;
      if (!Array.isArray(r.medicationType)) return;

      const slots = Array.isArray(r.timeSlot)
        ? r.timeSlot.map(normalizeSlot)
        : [normalizeSlot(r.timeSlot)];

      r.medicationType.forEach((entry) => {
        const status = entry?.status;
        if (!status || status === "upcoming") return;

        const entryDate = moment(entry.date);
        if (!entryDate.isValid()) return;
        if (entryDate.isBefore(viewStart) || entryDate.isAfter(viewEnd)) return;

        const dateStr = entryDate.utc().format("YYYY-MM-DD");
        const entrySlot = entry.timeSlot || slots[0] || "";
        const key = `${dateStr}_${entrySlot}_${r.medicationId}`;

        if (r.marType === "discontinued") {
          // Discontinued records: only dedup against other recovered entries.
          // Do NOT block against initialization-time existingKeys (document-date keys),
          // because the recorded statuses (missed/taken) need to be shown even when
          // their date matches the MAR document's start date.
          if (recoveredKeys.has(key)) return;
          recoveredKeys.add(key);
          existingKeys.add(key);
        } else {
          if (existingKeys.has(key)) return;
          existingKeys.add(key);
        }

        recoveredEntries.push({
          _id: r._id,
          date: `${dateStr}T12:00:00.000Z`,
          timeSlot: entrySlot,
          medicationId: r.medicationId,
          medicationType: [{ status, date: entryDate.toISOString() }],
          form3: r.form3 || null,
          // Past historical entries always show their stored numeric count,
          // regardless of whether the medicine was later changed to unlimited.
          medicineRemaining: r.medicineRemaining,
          totalMedicine: r.totalMedicine,
          isUnlimited: r.isUnlimited ?? false,
          client: r.client,
          staff: entry?.staff || r.staff,
          comments: entry?.comments || r.comments,
          _isHistoricalRecovery: true,
        });
      });
    });
    // ─────────────────────────────────────────────────────────────────────────────

    // virtualEntries are listed first so that dedupeByDateAndSlot (called in the
    // MonthCard data prop) encounters the correctly-statused virtual entry before
    // the raw DB record that may have medicationType:[] (empty, renders gray).
    // dedupeByDateAndSlot's preference logic handles the reverse order too, but
    // having virtuals first is the safest default.
    return [...virtualEntries, ...flattenedAppointments, ...recoveredEntries];
  }, [
    appointmentsFromBackend,
    rawAppointments,
    calendar,
    date,
    month,
    newCurrentWeek,
    selectedClient,
  ]);

  // Pre-index appointments by YYYY-MM-DD so each day column does O(1) lookup instead of O(n) filter
  const appointmentsByDate = useMemo(() => {
    const map = {};
    for (const item of appointments) {
      const key = moment(item.date).format("YYYY-MM-DD");
      if (!map[key]) map[key] = [];
      map[key].push(item);
    }
    return map;
  }, [appointments]);

  // useEffect(() => {
  //   getAllPatients("all");
  // }, []);

  // console.log('date hamza',date);

  // useEffect(() => {
  //   const _date = Array.from(Array(7).keys()).map((idx) => {
  //     const d = new Date(date);
  //     console.log('dtetet',d);
  //
  //     d.setDate(d.getDate() - d.getDay() + idx);
  //     return moment.tz(moment(d), profile?.user?.timeZone)?.toISOString();
  //   });
  //   setNewCurrentWeek(_date);
  // }, [date]);

  useEffect(() => {
    if (!date || !profile?.user?.timeZone) return;

    const base = moment.tz(date, profile.user.timeZone).startOf("day");

    const startOfWeek = base.clone().startOf("week");

    const week = Array.from({ length: 7 }, (_, idx) =>
      startOfWeek.clone().add(idx, "days").format("YYYY-MM-DD"),
    );

    setNewCurrentWeek(week);
  }, [date, profile?.user?.timeZone]);

  useEffect(() => {
    if (localSelectedClient?.id || userId || activeTab === "mar") getData();
    // }, [date, localSelectedClient, userId, calendar, activeTab, view, month]);
  }, [
    date,
    selectedClient,
    userId,
    calendar,
    activeTab,
    view,
    month,
    // localSelectedClient,
  ]);

  const getData = async () => {
    const clientId = localSelectedClient?.id || userId || selectedClient?.id;
    if (view === "list") {
      await getMar(`daily_${moment(date).format(dateFormat)}`, clientId);
    } else {
      if (calendar === "monthly") {
        const datesForMonthView = getDatesForMonthView(month);
        const pageStr = `monthly_${datesForMonthView[0]
          .toISOString()
          .slice(
            0,
            10,
          )}_${datesForMonthView[datesForMonthView.length - 1].toISOString().slice(0, 10)}`;
        await getCalendarViewData(pageStr, clientId);
      } else {
        const pageStr = `${calendar}_${moment(date).format(dateFormat)}`;
        localStorage.setItem("calendar_date", pageStr);
        await getCalendarViewData(pageStr, clientId);
      }
    }

    setScheduleLoading(false);
  };

  const handleNextPreDay = (val) => {
    let newDate = new Date(date);
    if (val === "next") {
      newDate.setDate(newDate.getDate() + 1);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    newDate.setHours(0, 0, 0, 0);
    localStorage.setItem(
      "calendar_date",
      `daily_${newDate.toISOString().slice(0, 10)}`,
    );
    startTransition(() => setDate(newDate));
  };

  const handleNextPreWeek = (val) => {
    let newDate = new Date(date);
    newDate.setDate(newDate.getDate() - newDate.getDay());
    if (val === "next") {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    newDate.setHours(0, 0, 0, 0);
    let newDateString = newDate.toISOString().slice(0, 10);

    localStorage.setItem("calendar_date", `${calendar}_${newDateString}`);
    startTransition(() => setDate(newDate));
  };

  const handleMonthChange = (value) => {
    let newDate;

    if (typeof value === "string" && (value === "next" || value === "pre")) {
      newDate = new Date(month);
      newDate.setMonth(newDate.getMonth() + (value === "next" ? 1 : -1));
    } else {
      newDate = value;
    }

    newDate.setHours(0, 0, 0, 0);

    const datesForMonthView = getDatesForMonthView(newDate);
    localStorage.setItem(
      "calendar_date",
      `monthly_${datesForMonthView[0]
        .toISOString()
        .slice(
          0,
          10,
        )}_${datesForMonthView[datesForMonthView.length - 1].toISOString().slice(0, 10)}`,
    );
    startTransition(() => setMonth(newDate));
  };

  const getWeekHeading = (days) => {
    const a = moment(days?.[0]);
    const b = moment(days?.[6]);
    const dd = (n) => String(n).padStart(2, "0");
    const aMonth = a.format("MMM").toUpperCase();
    const bMonth = b.format("MMM").toUpperCase();
    const year = b.format("YYYY");
    if (a.month() === b.month()) {
      return `${aMonth} ${dd(a.date())} – ${dd(b.date())}, ${year}`;
    }
    return `${aMonth} ${dd(a.date())} – ${bMonth} ${dd(b.date())}, ${year}`;
  };

  const handleCalendar = (val) => {
    clearMarCache();
    if (val === "monthly") {
      setCalendar(val);
      localStorage.setItem("calendar_view", `${val}`);
      getDatesForMonthView(month);
    } else if (val === "weekly") {
      setCalendar(val);
      localStorage.setItem("calendar_view", `${val}`);
      localStorage.setItem(
        "calendar_date",
        `${val}_${moment(date).format(dateFormat)}`,
      );
    } else if (val === "daily") {
      dispatch(setMar({ count: 0, newMar: [] }));
      dispatch(setCalendarEntries([]));
      setCalendar(val);
      localStorage.setItem("calendar_view", `${val}`);
      localStorage.setItem(
        "calendar_date",
        `${val}_${moment(date).format(dateFormat)}`,
      );
      const _date = `${new Date().getFullYear()}-${(new Date().getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${new Date().getDate().toString().padStart(2, "0")}`;
      setDate(_date);
    } else {
      setCalendar(val);
      localStorage.setItem("calendar_view", `${val}`);
      localStorage.setItem(
        "calendar_date",
        `${val}_${moment(date).format(dateFormat)}`,
      );
    }
  };

  const handleDateConverter = (date, timeslot = null) => {
    const currentDate = new Date(date);
    const timeString = timeslot;

    if (timeslot) {
      const [time, modifier] = timeString.split(" ");
      let [hours, minutes] = time.split(":").map(Number);

      if (modifier === "PM" && hours !== 12) {
        hours += 12;
      }
      if (modifier === "AM" && hours === 12) {
        hours = 0;
      }

      let newdate = currentDate.setHours(hours, minutes, 0, 0);
      return moment(newdate).format(dateFormat);
    } else {
      return moment(date).format(dateFormat);
    }
  };

  const openPRN = () => {
    setIsNew(true);
    setPRNShow(true);
  };
  const openPRNView = (data, date) => {
    setIsNew(false);
    prnRef?.current?.openView(data, date);
    setPRNShow(true);
  };
  const prnClose = () => setPRNShow(false);

  const getDatesForMonthView = (_month) => {
    const currentMonth = moment(_month);
    const previousMonthEnd = moment(currentMonth)
      .subtract(1, "month")
      .endOf("month");

    let lastSunday = previousMonthEnd.clone().endOf("week").subtract(6, "days");
    const diffrence = currentMonth.diff(lastSunday, "days") >= 6;

    const startDate = diffrence ? currentMonth.clone() : lastSunday.clone();
    const monthEnd = moment(_month).endOf("month");
    const daysNeeded = monthEnd.diff(startDate, "days") + 1;
    const totalCells = daysNeeded > 35 ? 42 : 35;

    let _dates = [];
    for (let index = 0; index < totalCells; index++) {
      _dates.push(diffrence ? currentMonth.clone() : lastSunday.clone());
      diffrence ? currentMonth.add(1, "day") : lastSunday.add(1, "day");
    }

    localStorage.setItem(
      "calendar_date",
      `monthly_${_dates[0].toISOString().slice(0, 10)}_${_dates[
        _dates.length - 1
      ]
        .toISOString()
        .slice(0, 10)}`,
    );

    return _dates;
  };

  const [opened, { open, close }] = useDisclosure(false);
  const [
    checkboxModalOpened,
    { open: openCheckboxModal, close: closeCheckboxModal },
  ] = useDisclosure(false);
  const [selectedMonthDate, setSelectedMonthDate] = useState(null);

  const dedupeByDateAndSlot = (data = []) => {
    const map = new Map();
    const prnItems = [];

    data?.forEach((item) => {
      // PRN entries are never deduped — multiple administrations on the same
      // day are all valid and must each render their own card.
      if (item.marType === "PRN") {
        prnItems.push(item);
        return;
      }

      const key = `${moment(item.date).format("YYYY-MM-DD")}_${item.timeSlot}_${
        item.medicationId || item._id
      }`;

      if (!map?.has(key)) {
        map?.set(key, item);
      } else {
        // Prefer entries with an actual status over raw empty-array records.
        // When a schedule medicine is first created its MAR document has
        // medicationType:[] (no status), which renders gray. Virtual entries
        // generated for past dates carry the correct "Missed" status — so if
        // the existing map entry has an empty medicationType array and the new
        // candidate has a real status, replace it.
        const existing = map.get(key);
        const existingIsEmpty =
          Array.isArray(existing?.medicationType) &&
          existing.medicationType.length === 0;
        const incomingHasStatus =
          Array.isArray(item?.medicationType) && item.medicationType.length > 0;
        if (existingIsEmpty && incomingHasStatus) {
          map.set(key, item);
        }
      }
    });

    return [...Array.from(map?.values()), ...prnItems];
  };

  const getStatus = (item, targetDate) => {
    const medType = item?.medicationType;
    if (!medType) return "";
    if (typeof medType === "string") return medType;
    if (Array.isArray(medType)) {
      const dateToCheck = targetDate || item.date;
      const targetDateStr = moment(dateToCheck).format("YYYY-MM-DD");
      const matching = medType.filter(
        (m) =>
          moment(m.date).format("YYYY-MM-DD") === targetDateStr &&
          (!m.timeSlot || m.timeSlot === item.timeSlot),
      );
      // Use the LAST matching entry (latest status for this date).
      // For "upcoming"/"Missed" on TODAY: the stored status may be stale —
      // it was stamped at page-load time (inside generateEntry) and the clock
      // has since moved past the slot time.  Fall through to the slot-time
      // recompute so the switch from upcoming → Missed happens without a refresh.
      if (matching.length > 0) {
        const lastStatus = typeof matching[matching.length - 1].status === "string"
          ? matching[matching.length - 1].status
          : "";
        const isToday = moment(dateToCheck).isSame(moment(), "day");
        if (lastStatus !== "upcoming" && lastStatus !== "Missed")
          return lastStatus;
        if (!isToday) return lastStatus;
        // fall through to slot-time recompute
      }
      // No matching entry for this date — fall back based on whether the date is past or future.
      // Previously this always returned "upcoming" which caused past start-dates of newly created
      // medications (whose medicationType[] is empty) to render as gray instead of red (Missed).
      const nowInBiz = moment();
      const startOfToday = nowInBiz.clone().startOf("day");
      const checkDate = moment(dateToCheck).startOf("day");
      if (checkDate.isBefore(startOfToday, "day")) return "Missed";
      if (checkDate.isAfter(startOfToday, "day")) return "upcoming";
      // Today: check whether the slot time has already passed
      const slotMoment = item?.timeSlot
        ? moment(item.timeSlot, ["hh:mm A", "HH:mm"])
        : null;
      if (slotMoment && slotMoment.isValid()) {
        const slotToday = startOfToday.clone().set({
          hour: slotMoment.hours(),
          minute: slotMoment.minutes(),
          second: 0,
          millisecond: 0,
        });
        return nowInBiz.isSameOrAfter(slotToday) ? "Missed" : "upcoming";
      }
      return "upcoming";
    }
    return "";
  };

  // Recursively resolve whether a medicationType value represents PRN.
  // Handles plain strings, arrays, and double-stringified JSON (as stored in DB).
  const resolveMedTypePRN = (medType) => {
    if (!medType) return false;
    if (typeof medType === "string") {
      if (medType.toUpperCase() === "PRN") return true;
      try {
        return resolveMedTypePRN(JSON.parse(medType));
      } catch (e) {
        return false;
      }
    }
    if (Array.isArray(medType)) {
      if (medType.length === 0) return false;
      const last = medType[medType.length - 1];
      return resolveMedTypePRN(last?.status);
    }
    return false;
  };

  // True if the medicine itself is of type PRN (checks form3 definition then record)
  const isPRNMed = (item) => {
    if (item?.form3?.medicationType !== undefined) {
      if (resolveMedTypePRN(item.form3.medicationType)) return true;
    }
    return resolveMedTypePRN(item?.medicationType);
  };

  // Memoized month date array — replaces the former let/if that ran on every render
  const datesForMonthView = useMemo(
    () => (calendar === "monthly" ? getDatesForMonthView(month) : []),
    [calendar, month],
  );

  // Pre-computed cards + allData per week-day column (eliminates per-column filter+dedupe)
  const weeklyDataByDate = useMemo(() => {
    if (calendar !== "weekly" || !newCurrentWeek?.length) return {};
    const result = {};
    for (const dateISO of newCurrentWeek) {
      const dateKey = moment(dateISO).format("YYYY-MM-DD");
      const dayItems = appointmentsByDate[dateKey] || [];
      const scheduledCards = dedupeByDateAndSlot(
        dayItems.filter(
          (x) =>
            !isPRNMed(x) &&
            !["PRN", "DISCONTINUED"].includes(
              getStatus(x, dateISO)?.toUpperCase(),
            ),
        ),
      );
      const prnCards = dayItems.filter(
        (x) => isPRNMed(x) && getStatus(x, dateISO)?.toUpperCase() === "PRN",
      );
      const allScheduled = dedupeByDateAndSlot(
        dayItems.filter(
          (x) => !isPRNMed(x) && getStatus(x, dateISO)?.toUpperCase() !== "PRN",
        ),
      );
      result[dateKey] = {
        cards: [...scheduledCards, ...prnCards],
        allData: [...allScheduled, ...prnCards],
      };
    }
    return result;
  }, [appointments, appointmentsByDate, newCurrentWeek, calendar]);

  // Pre-computed deduped data per monthly cell
  const monthlyDataByDate = useMemo(() => {
    if (calendar !== "monthly" || !datesForMonthView.length) return {};
    const result = {};
    for (const dateObj of datesForMonthView) {
      const dateKey = moment(dateObj).format("YYYY-MM-DD");
      const dayItems = appointmentsByDate[dateKey] || [];
      result[dateKey] = dedupeByDateAndSlot(
        dayItems.filter((x) => {
          const status = getStatus(x, dateObj)?.toUpperCase();
          if (isPRNMed(x)) return status === "PRN";
          return status !== "DISCONTINUED";
        }),
      );
    }
    return result;
  }, [appointments, appointmentsByDate, calendar, datesForMonthView]);

  // Pre-computed cards, TodayDateCard data, and allData per time-slot bucket for daily view
  const dailyDataBySlot = useMemo(() => {
    if (calendar !== "daily") return {};
    const dateKey = moment(date).format("YYYY-MM-DD");
    const dayItems = appointmentsByDate[dateKey] || [];

    // allData is the same for every card in daily view — compute once
    const allScheduled = dedupeByDateAndSlot(
      dayItems.filter(
        (x) => !isPRNMed(x) && getStatus(x, date)?.toUpperCase() !== "PRN",
      ),
    );
    const allPrn = dayItems.filter(
      (x) => isPRNMed(x) && getStatus(x, date)?.toUpperCase() === "PRN",
    );
    const allData = [...allScheduled, ...allPrn].sort(
      (a, b) => new Date(a?.date) - new Date(b?.date),
    );

    const bySlot = {};
    for (const slotBucket of TIME_SLOTS) {
      const scheduledCards = dedupeByDateAndSlot(
        dayItems.filter((x) => {
          if (isPRNMed(x)) return false;
          const status = getStatus(x, date)?.toUpperCase();
          return (
            !["PRN", "DISCONTINUED"].includes(status) &&
            slotBucket.slots.includes(x?.timeSlot)
          );
        }),
      );
      const prnCards = dayItems.filter((x) => {
        if (!isPRNMed(x) || getStatus(x, date)?.toUpperCase() !== "PRN")
          return false;
        const administeredAt =
          x?.administeredAt || x?.medicationType?.[0]?.administeredAt;
        if (!administeredAt) return false;
        const roundedTime = roundTimeToNearestHalfHour(
          moment(administeredAt)
            .tz(timeZone || moment.tz.guess())
            .format("hh:mm A"),
        );
        return slotBucket.slots.includes(roundedTime);
      });
      const todayCardItems = dayItems
        .filter((x) => {
          const status = getStatus(x, date)?.toUpperCase();
          if (isPRNMed(x)) {
            if (status !== "PRN") return false;
            const administeredAt =
              x?.administeredAt || x?.medicationType?.[0]?.administeredAt;
            if (!administeredAt) return false;
            const roundedTime = roundTimeToNearestHalfHour(
              moment(administeredAt)
                .tz(timeZone || moment.tz.guess())
                .format("hh:mm A"),
            );
            return slotBucket.slots.includes(roundedTime);
          }
          return status !== "PRN" && slotBucket.slots.includes(x?.timeSlot);
        })
        .sort((a, b) => new Date(a?.date) - new Date(b?.date));

      bySlot[slotBucket.title] = {
        cards: [...scheduledCards, ...prnCards].sort(
          (a, b) => new Date(a?.date) - new Date(b?.date),
        ),
        todayCardItems,
        allData,
      };
    }
    return bySlot;
  }, [appointments, appointmentsByDate, calendar, date]);

  // ── Unique ordered medication list for weekly row-grid ──────────────────
  const weeklyMedRows = useMemo(() => {
    if (calendar !== "weekly" || !newCurrentWeek?.length) return [];
    const seen = new Set();
    const rows = [];
    for (const dateISO of newCurrentWeek) {
      const dateKey = moment(dateISO).format("YYYY-MM-DD");
      const cards = weeklyDataByDate[dateKey]?.cards || [];
      for (const card of cards) {
        // Use medicationId+timeSlot as key so multiple slots of the same
        // medication each get their own row in the weekly grid.
        const key = `${card.medicationId || card._id}__${card.timeSlot || ""}`;
        if (!seen.has(key)) {
          seen.add(key);
          rows.push(card);
        }
      }
    }
    return rows;
  }, [weeklyDataByDate, newCurrentWeek, calendar]);

  // ── Unique ordered medication list for daily row-grid ────────────────────
  const dailyMedRows = useMemo(() => {
    if (calendar !== "daily") return [];
    const seen = new Set();
    const rows = [];
    for (const slot of TIME_SLOTS) {
      const cards = dailyDataBySlot[slot.title]?.cards || [];
      for (const card of cards) {
        // Both PRN and scheduled meds: key by medicationId only so all
        // timeslots of the same medication collapse to one row.
        const key = `${card.medicationId || card._id}`;
        if (!seen.has(key)) {
          seen.add(key);
          rows.push(card);
        }
      }
    }
    return rows;
  }, [dailyDataBySlot, calendar]);

  // ── Helper: find the specific card for a med+date in weekly grid ─────────
  const getWeeklyCardForMedAndDate = (medRow, dateISO) => {
    const dateKey = moment(dateISO).format("YYYY-MM-DD");
    const cards = weeklyDataByDate[dateKey]?.cards || [];
    const medId = medRow.medicationId || medRow._id;
    const timeSlot = medRow.timeSlot || "";
    // Match by both medicationId and timeSlot so each slot gets its own cell.
    return (
      cards.find(
        (c) => (c.medicationId || c._id) === medId && c.timeSlot === timeSlot,
      ) || null
    );
  };

  // ── Helper: find the specific card(s) for a med+slot in daily grid ─────────
  // For PRN meds: returns ALL matching cards bundled (multiple admins in same bucket).
  // For scheduled meds: returns ALL matching timeslots in the bucket bundled;
  //   primary card carries `scheduledExtras` array when there are multiple.
  const getDailyCardForMedAndSlot = (medRow, slotTitle) => {
    const cards = dailyDataBySlot[slotTitle]?.cards || [];
    const medId = medRow.medicationId || medRow._id;
    const isPRN = isPRNMed(medRow);
    if (isPRN) {
      const matches = cards.filter((c) => (c.medicationId || c._id) === medId);
      if (matches.length === 0) return null;
      const [primary, ...rest] = matches;
      return rest.length > 0 ? { ...primary, extras: rest } : primary;
    }
    // Scheduled: match all timeslots of this medication in the bucket
    const matches = cards.filter((c) => (c.medicationId || c._id) === medId);
    if (matches.length === 0) return null;
    const [primary, ...rest] = matches;
    return rest.length > 0 ? { ...primary, scheduledExtras: rest } : primary;
  };

  // ── Status color resolver ────────────────────────────────────────────────
  const resolveCardStatus = (card, dateArg, isPRN) => {
    if (!card) return null;
    if (isPRN) return "PRN";
    const raw = getSpecificMedicationStatus(
      card?.medicationType,
      dateArg,
      card?.timeSlot,
    );
    return raw || "upcoming";
  };

  return (
    <>
      <div className={classes.content}>
        <Flex className={classes.tabsAndBtnBar}>
          <div className={classes.tabContainer}>
            {view !== "list" && (
              <Box className={classes.calendarTabsParent}>
                <div
                  // className={`${classes.monthlyBtn} ${
                  //   calendar === "monthly" ? classes.activeWeekToday : ""
                  // }`}
                  className={`${classes.calendarTabsBtn} ${
                    calendar === "monthly" && classes.calendarTabsBtnActive
                  }`}
                  onClick={() => handleCalendar("monthly")}
                >
                  {/* <img src={"/assets/images/week-icon.png"} /> */}
                  <p>Month</p>
                </div>
                <div
                  id="medication-week"
                  // className={`${classes.weeklyBtn} ${
                  //   calendar === "weekly" ? classes.activeWeekToday : ""
                  // }`}
                  className={`${classes.calendarTabsBtn} ${
                    calendar === "weekly" && classes.calendarTabsBtnActive
                  }`}
                  onClick={() => handleCalendar("weekly")}
                >
                  {/* <img src={"/assets/images/week-icon.png"} /> */}
                  <p>Week</p>
                </div>
                <div
                  id="medication-today"
                  // className={`${classes.todayBtn} ${
                  //   calendar === "daily" ? classes.activeWeekToday : ""
                  // }`}
                  className={`${classes.calendarTabsBtn} ${
                    calendar === "daily" && classes.calendarTabsBtnActive
                  }`}
                  onClick={() => handleCalendar("daily")}
                >
                  {/* <img src={"/assets/images/today-icon.png"} /> */}
                  <p>Today</p>
                </div>
              </Box>
            )}
          </div>
          <AddMedicationButton
            userId={userId}
            selectedClient={selectedClient}
          />
        </Flex>

        <Box className={classes.rightBtns}>
          {calendar === "monthly" && (
            <Box>
              <MonthPickerInput
                value={month}
                className={classes.monthPicker}
                placeholder="Select Month"
                onChange={handleMonthChange}
                icon={<FiCalendar />}
              />
            </Box>
          )}
          <Button onClick={openPRN} className={classes.prnButton}>
            Administer PRN
          </Button>
          <Button className={classes.createReportBtn} onClick={open}>
            Create Report
          </Button>
        </Box>
        <div className={classes.scheduleDetail}>
          {!userId && (
            <UserDetail
              isClient={false}
              setLocalSelectedClient={setLocalSelectedClient}
            />
          )}
          {view === "calendar" ? (
            <div
              style={{ width: "100%", overflowX: "auto" }}
              // className={
              //   (userId && !userId) || !show
              //     ? classes.scheduleDetailRight100
              //     : classes.scheduleDetailRight
              // }
            >
              <div className={classes.wrapper}>
                {loading ? (
                  <>
                    {calendar === "weekly" ? (
                      <div className={classes.mainContainer}>
                        <div className={classes.dateAndCarousalRow}>
                          <div />
                          <div className={classes.calendarNavCenter}>
                            <div className={classes.carouselWrapper}>
                              <ActionIcon variant="default" radius="xl">
                                <FiChevronLeft size={16} />
                              </ActionIcon>
                              <Skeleton height={14} width={200} radius="sm" />
                              <ActionIcon variant="default" radius="xl">
                                <FiChevronRight size={16} />
                              </ActionIcon>
                            </div>
                          </div>
                          <div className={classes.calendarRightAction}>
                            <Skeleton height={14} width={170} radius="sm" />
                          </div>
                        </div>
                        <div className={classes.marGridWrapper}>
                          <div
                            className={classes.marGrid}
                            style={{
                              gridTemplateColumns: "260px repeat(7, 170px)",
                            }}
                          >
                            {/* Header row */}
                            <div className={classes.marHeaderLabelCell}>
                              <Skeleton height={13} width={120} radius="sm" />
                            </div>
                            {[...Array(7)].map((_, i) => (
                              <div key={i} className={classes.marHeaderColCell}>
                                <Skeleton
                                  height={11}
                                  width={28}
                                  radius="sm"
                                  style={{ margin: "0 auto" }}
                                />
                                <Skeleton
                                  height={22}
                                  width={22}
                                  radius="sm"
                                  style={{ margin: "2px auto 0" }}
                                />
                              </div>
                            ))}
                            {/* Skeleton medication rows */}
                            {[...Array(5)].map((_, rowIdx) => (
                              <React.Fragment key={rowIdx}>
                                <div className={classes.marLabelCell}>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      marginBottom: 6,
                                    }}
                                  >
                                    <Skeleton
                                      height={22}
                                      width={22}
                                      radius="sm"
                                    />
                                    <Skeleton height={13} radius="sm" />
                                  </div>
                                  <Skeleton
                                    height={11}
                                    width="70%"
                                    radius="sm"
                                    mb={4}
                                  />
                                  <Skeleton
                                    height={11}
                                    width="50%"
                                    radius="sm"
                                  />
                                </div>
                                {[...Array(7)].map((_, colIdx) => (
                                  <div
                                    key={colIdx}
                                    className={classes.marEmptyCell}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Skeleton height={36} radius="sm" />
                                  </div>
                                ))}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : calendar === "daily" ? (
                      <div className={classes.mainContainer}>
                        <div className={classes.dateAndCarousalRow}>
                          <div />
                          <div className={classes.calendarNavCenter}>
                            <div className={classes.carouselWrapper}>
                              <ActionIcon variant="default" radius="xl">
                                <FiChevronLeft size={16} />
                              </ActionIcon>
                              <Skeleton height={14} width={200} radius="sm" />
                              <ActionIcon variant="default" radius="xl">
                                <FiChevronRight size={16} />
                              </ActionIcon>
                            </div>
                          </div>
                          <div className={classes.calendarRightAction}>
                            <Skeleton height={14} width={170} radius="sm" />
                          </div>
                        </div>
                        <div className={classes.marGridWrapper}>
                          <div
                            className={classes.marGrid}
                            style={{
                              gridTemplateColumns: `260px repeat(${TIME_SLOTS.length}, 170px)`,
                            }}
                          >
                            {/* Header row */}
                            <div className={classes.marHeaderLabelCell}>
                              <Skeleton height={13} width={120} radius="sm" />
                            </div>
                            {TIME_SLOTS.map((_, i) => (
                              <div key={i} className={classes.marHeaderColCell}>
                                <Skeleton
                                  height={11}
                                  width={60}
                                  radius="sm"
                                  style={{ margin: "0 auto" }}
                                />
                              </div>
                            ))}
                            {/* Skeleton medication rows */}
                            {[...Array(5)].map((_, rowIdx) => (
                              <React.Fragment key={rowIdx}>
                                <div className={classes.marLabelCell}>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      marginBottom: 6,
                                    }}
                                  >
                                    <Skeleton
                                      height={22}
                                      width={22}
                                      radius="sm"
                                    />
                                    <Skeleton height={13} radius="sm" />
                                  </div>
                                  <Skeleton
                                    height={11}
                                    width="70%"
                                    radius="sm"
                                    mb={4}
                                  />
                                  <Skeleton
                                    height={11}
                                    width="50%"
                                    radius="sm"
                                  />
                                </div>
                                {TIME_SLOTS.map((_, colIdx) => (
                                  <div
                                    key={colIdx}
                                    className={classes.marEmptyCell}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Skeleton height={36} radius="sm" />
                                  </div>
                                ))}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={classes.mainContainer}>
                        <div className={classes.dateAndCarousalRow}>
                          <div />
                          <div className={classes.calendarNavCenter}>
                            <div className={classes.carouselWrapper}>
                              <ActionIcon
                                variant="default"
                                radius="xl"
                                onClick={() => handleMonthChange("pre")}
                              >
                                <FiChevronLeft size={16} />
                              </ActionIcon>
                              <p className="dateHdng">
                                {moment(month)
                                  .format("MMMM YYYY")
                                  .toUpperCase()}
                              </p>
                              <ActionIcon
                                variant="default"
                                radius="xl"
                                onClick={() => handleMonthChange("next")}
                              >
                                <FiChevronRight size={16} />
                              </ActionIcon>
                            </div>
                          </div>
                          <div className={classes.calendarRightAction}>
                            <Skeleton height={16} width={180} radius="sm" />
                          </div>
                        </div>
                        <div className={classes.calendarScrollWrapper}>
                          {WEEK_DAYS.map((day, i) => (
                            <div
                              key={day}
                              className={classes.columnWithHeaderMonth}
                              style={{ minWidth: "120px" }}
                            >
                              <div
                                className={classes.sliderItemHeader}
                                style={{ padding: "10px 8px" }}
                              >
                                <Skeleton height={12} radius="sm" />
                              </div>
                              <div
                                className={classes.columnBodyMonth}
                                style={{ padding: 0 }}
                              >
                                {[...Array(5)].map((_, j) => (
                                  <div
                                    key={j}
                                    style={{
                                      padding: "8px 6px",
                                      height: "190px",
                                      border: "1px solid #E5E5E5",
                                      borderLeft: "none",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 5,
                                    }}
                                  >
                                    <Skeleton
                                      height={20}
                                      width={20}
                                      radius="sm"
                                    />
                                    <Skeleton height={18} radius="sm" />
                                    <Skeleton height={18} radius="sm" />
                                    <Skeleton height={18} radius="sm" />
                                    <Skeleton height={18} radius="sm" />
                                    <Skeleton height={18} radius="sm" />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {calendar === "weekly" ? (
                      <div className={classes.mainContainer}>
                        {/* <Box className={classes.cuationsBox}>
                          <Cuations />
                        </Box> */}

                        <div className={classes.dateAndCarousalRow}>
                          <div className={classes.calendarClientInfo}>
                            {selectedClient && (
                              <>
                                <div className={classes.calendarClientAvatar}>
                                  {decrypt(selectedClient?.imageurl) ? (
                                    <img
                                      src={decrypt(selectedClient.imageurl)}
                                      alt={selectedClient.name}
                                    />
                                  ) : (
                                    <Text
                                      className={
                                        classes.calendarClientAvatarInitials
                                      }
                                    >
                                      {(selectedClient.name || "")
                                        .split(" ")
                                        .filter(Boolean)
                                        .map((w) => w[0])
                                        .join("")
                                        .toUpperCase()
                                        .slice(0, 2)}
                                    </Text>
                                  )}
                                </div>
                                <div
                                  className={classes.calendarClientNameStack}
                                >
                                  <Text className={classes.calendarClientName}>
                                    {selectedClient.name}
                                  </Text>
                                  {(selectedClient?.id ||
                                    selectedClient?._id) && (
                                    <Text className={classes.calendarClientId}>
                                      {String(
                                        selectedClient?.id ||
                                          selectedClient?._id,
                                      )
                                        .slice(0, 6)
                                        .toLowerCase()}
                                    </Text>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          <div className={classes.calendarNavCenter}>
                            <div className={classes.carouselWrapper}>
                              <ActionIcon
                                variant="default"
                                radius="xl"
                                onClick={() => handleNextPreWeek("pre")}
                              >
                                <FiChevronLeft size={16} />
                              </ActionIcon>
                              {newCurrentWeek && (
                                <p className="dateHdng">
                                  {getWeekHeading(newCurrentWeek)}
                                </p>
                              )}
                              <ActionIcon
                                variant="default"
                                radius="xl"
                                onClick={() => handleNextPreWeek("next")}
                              >
                                <FiChevronRight size={16} />
                              </ActionIcon>
                            </div>
                          </div>
                          <div className={classes.calendarRightAction}>
                            <Text
                              className={classes.multipleMedBtn}
                              onClick={openCheckboxModal}
                            >
                              Select Multiple Medication
                            </Text>
                          </div>
                        </div>
                        <MarMedicationModal
                          data={dedupeByDateAndSlot(
                            appointments?.filter((x) => {
                              // Must match the Sunday-based week built for
                              // newCurrentWeek (startOf("week")). Using isoWeek
                              // (Monday-based) here dropped the Sunday column,
                              // which belongs to the previous ISO week.
                              const startOfWeek = newCurrentWeek?.length
                                ? moment(newCurrentWeek[0]).startOf("day")
                                : moment(date).startOf("week");
                              const endOfWeek = newCurrentWeek?.length
                                ? moment(
                                    newCurrentWeek[newCurrentWeek.length - 1],
                                  ).endOf("day")
                                : moment(date).endOf("week");
                              const appointmentDate = moment(x?.date);
                              const isNotDiscontinued =
                                getStatus(x, x.date)?.toUpperCase() !==
                                "DISCONTINUED";
                              return (
                                !isPRNMed(x) &&
                                isNotDiscontinued &&
                                appointmentDate.isBetween(
                                  startOfWeek,
                                  endOfWeek,
                                  null,
                                  "[]",
                                )
                              );
                            }),
                          )}
                          calendar={calendar}
                          close={closeCheckboxModal}
                          opened={checkboxModalOpened}
                          date={newCurrentWeek}
                          currentDate={date}
                        />

                        {/* Row-per-medication grid */}
                        <div className={classes.marGridWrapper}>
                          <div
                            className={classes.marGrid}
                            style={{
                              gridTemplateColumns: `${marLabelCollapsed ? "36px" : "260px"} repeat(${newCurrentWeek?.length || 7}, 170px)`,
                            }}
                          >
                            {/* Header row */}
                            <div
                              className={`${classes.marHeaderLabelCell}${marLabelCollapsed ? ` ${classes.marHeaderLabelCellCollapsed}` : ""}`}
                              onClick={
                                marLabelCollapsed
                                  ? () => setMarLabelCollapsed(false)
                                  : undefined
                              }
                            >
                              {marLabelCollapsed ? (
                                <ActionIcon
                                  variant="transparent"
                                  size="sm"
                                  className={classes.collapseToggleBtn}
                                  title="Expand medication names"
                                  onClick={() => setMarLabelCollapsed(false)}
                                >
                                  <FiChevronsRight size={14} />
                                </ActionIcon>
                              ) : (
                                <>
                                  <Text className={classes.marHeaderLabelText}>
                                    Medication / Dose
                                  </Text>
                                  <ActionIcon
                                    variant="transparent"
                                    size="sm"
                                    className={classes.collapseToggleBtn}
                                    title="Collapse medication names"
                                    onClick={() => setMarLabelCollapsed(true)}
                                  >
                                    <FiChevronsLeft size={14} />
                                  </ActionIcon>
                                </>
                              )}
                            </div>
                            {(newCurrentWeek || []).map((dateISO) => {
                              const isToday = moment(dateISO).isSame(
                                moment(),
                                "day",
                              );
                              return (
                                <div
                                  key={dateISO}
                                  className={
                                    isToday
                                      ? classes.marHeaderColCellToday
                                      : classes.marHeaderColCell
                                  }
                                >
                                  <p
                                    className={
                                      isToday
                                        ? classes.marHeaderDayNameToday
                                        : classes.marHeaderDayName
                                    }
                                  >
                                    {moment(dateISO).format("ddd")}
                                  </p>
                                  <p
                                    className={
                                      isToday
                                        ? classes.marHeaderDateToday
                                        : classes.marHeaderDate
                                    }
                                  >
                                    {moment(dateISO).format("DD")}
                                  </p>
                                </div>
                              );
                            })}
                            {/* Data rows — grouped by medicationId so the label
                                cell spans all slot rows of the same medicine */}
                            {(() => {
                              // Group rows by medicationId preserving order
                              const groups = [];
                              const seenMed = new Map();
                              for (const row of weeklyMedRows) {
                                const medId = row.medicationId || row._id;
                                if (seenMed.has(medId)) {
                                  groups[seenMed.get(medId)].slots.push(row);
                                } else {
                                  seenMed.set(medId, groups.length);
                                  groups.push({ medId, slots: [row] });
                                }
                              }
                              return groups.flatMap(({ slots }) =>
                                slots.map((medRow, slotIndex) => {
                                  const isPRN = isPRNMed(medRow);
                                  const medName =
                                    firstLetterCap(
                                      medRow?.form3?.medicationName,
                                    ) || "-";
                                  const dosage = medRow?.form3?.dosage
                                    ? `${medRow.form3.dosage}${medRow.form3.unit || ""}`
                                    : null;
                                  const formDisplay = [
                                    medRow?.form3?.ingestionMode,
                                    medRow?.form3?.dosageForm,
                                  ]
                                    .filter(Boolean)
                                    .join(" ");
                                  return (
                                    <React.Fragment
                                      key={`${medRow.medicationId || medRow._id}__${medRow.timeSlot || ""}`}
                                    >
                                      {/* Label cell — one per slot row (each slot gets its own card) */}
                                      {(() => {
                                        const isNarcotic =
                                          medRow?.form3?.isNarcotic === "Yes";
                                        // Show only this slot's time (not all slots combined)
                                        const timeSlotDisplay = isPRN
                                          ? "PRN"
                                          : medRow.timeSlot
                                            ? moment(
                                                medRow.timeSlot,
                                                "hh:mm A",
                                              ).format("h:mm A")
                                            : null;
                                        return (
                                          <div
                                            className={`${classes.marLabelCell}${marLabelCollapsed ? ` ${classes.marLabelCellCollapsed}` : ""}`}
                                            style={{
                                              ...(marLabelCollapsed
                                                ? {
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    gap: 0,
                                                    padding: "4px",
                                                  }
                                                : {}),
                                            }}
                                            onClick={
                                              marLabelCollapsed
                                                ? () =>
                                                    setMarLabelCollapsed(false)
                                                : () =>
                                                    setActiveMedInfo({
                                                      row: medRow,
                                                      slots,
                                                    })
                                            }
                                            title={
                                              marLabelCollapsed
                                                ? medName
                                                : undefined
                                            }
                                          >
                                            {!marLabelCollapsed ? (
                                              <>
                                                <div
                                                  className={
                                                    classes.marLabelTitleRow
                                                  }
                                                >
                                                  <span
                                                    className={`${classes.marLabelPillIconWrap} ${isNarcotic ? classes.marLabelPillIconWrapNarcotic : classes.marLabelPillIconWrapNormal}`}
                                                  >
                                                    <LuPill size={14} />
                                                  </span>
                                                  <p
                                                    className={
                                                      classes.marLabelMedName
                                                    }
                                                  >
                                                    {dosage
                                                      ? `${medName} (${dosage})`
                                                      : medName}
                                                  </p>
                                                  {isNarcotic && (
                                                    <span
                                                      className={
                                                        classes.marNarcoticBadge
                                                      }
                                                    >
                                                      Narcotic
                                                    </span>
                                                  )}
                                                </div>
                                                {formDisplay ? (
                                                  <p
                                                    className={
                                                      classes.marLabelStrength
                                                    }
                                                  >
                                                    {formDisplay}
                                                  </p>
                                                ) : null}
                                                <div
                                                  className={
                                                    classes.marLabelBadgeRow
                                                  }
                                                >
                                                  {medRow?.form3?.frequency ? (
                                                    <p
                                                      className={
                                                        isPRN
                                                          ? classes.marLabelFreqPrn
                                                          : classes.marLabelFreq
                                                      }
                                                    >
                                                      {medRow.form3.frequency}
                                                    </p>
                                                  ) : null}
                                                  {timeSlotDisplay ? (
                                                    <p
                                                      className={
                                                        classes.marLabelTimeSlot
                                                      }
                                                    >
                                                      {timeSlotDisplay}
                                                    </p>
                                                  ) : null}
                                                </div>
                                                {medRow?.form3?.sig ? (
                                                  <p
                                                    className={
                                                      classes.marLabelSig
                                                    }
                                                  >
                                                    {medRow.form3.sig}
                                                  </p>
                                                ) : null}
                                                {medRow?.form3
                                                  ?.medicationInstruction ? (
                                                  <p
                                                    className={
                                                      classes.marLabelMedInfo
                                                    }
                                                  >
                                                    {
                                                      medRow.form3
                                                        .medicationInstruction
                                                    }
                                                  </p>
                                                ) : null}
                                              </>
                                            ) : (
                                              <span
                                                className={`${classes.marLabelPillIconWrap} ${isNarcotic ? classes.marLabelPillIconWrapNarcotic : classes.marLabelPillIconWrapNormal}`}
                                              >
                                                <LuPill size={12} />
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })()}

                                      {/* Status cells per day */}
                                      {(newCurrentWeek || []).map((dateISO) => {
                                        const card = getWeeklyCardForMedAndDate(
                                          medRow,
                                          dateISO,
                                        );
                                        const allDataForDay =
                                          weeklyDataByDate[
                                            moment(dateISO).format("YYYY-MM-DD")
                                          ]?.allData || [];
                                        return card ? (
                                          <ScheduleCard
                                            key={`${medRow.medicationId || medRow._id}-${medRow.timeSlot || ""}-${dateISO}`}
                                            calendar={calendar}
                                            date={moment(dateISO).format(
                                              "MM/DD/YYYY",
                                            )}
                                            data={card}
                                            prn={isPRN}
                                            onOpen={
                                              isPRN ? openPRNView : undefined
                                            }
                                            allData={allDataForDay}
                                            showMedDetails={marLabelCollapsed}
                                            timeZone={timeZone}
                                          />
                                        ) : (
                                          <div
                                            key={`${medRow.medicationId || medRow._id}-${dateISO}-empty`}
                                            className={classes.marEmptyCell}
                                          >
                                            —
                                          </div>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                }),
                              );
                            })()}
                            {weeklyMedRows.length === 0 && (
                              <div
                                style={{
                                  gridColumn: `1 / -1`,
                                  padding: "40px",
                                  textAlign: "center",
                                }}
                              >
                                <Text color="dimmed" size="sm">
                                  No medications scheduled for this week
                                </Text>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : calendar === "daily" ? (
                      <div className={classes.mainContainer}>
                        {/* <Box className={classes.cuationsBox}>
                          <Cuations />
                        </Box> */}
                        <div className={classes.dateAndCarousalRow}>
                          <div className={classes.calendarClientInfo}>
                            {selectedClient && (
                              <>
                                <div className={classes.calendarClientAvatar}>
                                  {decrypt(selectedClient?.imageurl) ? (
                                    <img
                                      src={decrypt(selectedClient?.imageurl)}
                                      alt={selectedClient.name}
                                    />
                                  ) : (
                                    <Text
                                      className={
                                        classes.calendarClientAvatarInitials
                                      }
                                    >
                                      {(selectedClient.name || "")
                                        .split(" ")
                                        .filter(Boolean)
                                        .map((w) => w[0])
                                        .join("")
                                        .toUpperCase()
                                        .slice(0, 2)}
                                    </Text>
                                  )}
                                </div>
                                <div
                                  className={classes.calendarClientNameStack}
                                >
                                  <Text className={classes.calendarClientName}>
                                    {selectedClient.name}
                                  </Text>
                                  {(selectedClient?.id ||
                                    selectedClient?._id) && (
                                    <Text className={classes.calendarClientId}>
                                      {String(
                                        selectedClient?.id ||
                                          selectedClient?._id,
                                      )
                                        .slice(0, 6)
                                        .toLowerCase()}
                                    </Text>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          <div className={classes.calendarNavCenter}>
                            <div className={classes.carouselWrapper}>
                              <ActionIcon
                                variant="default"
                                radius="xl"
                                onClick={() => handleNextPreDay("pre")}
                              >
                                <FiChevronLeft size={16} />
                              </ActionIcon>
                              <p className="dateHdng">
                                {moment(date).format("dddd MM/DD/YYYY")}
                              </p>
                              <ActionIcon
                                variant="default"
                                radius="xl"
                                onClick={() => handleNextPreDay("next")}
                              >
                                <FiChevronRight size={16} />
                              </ActionIcon>
                            </div>
                          </div>
                          <div className={classes.calendarRightAction}>
                            <Text
                              className={classes.multipleMedBtn}
                              onClick={openCheckboxModal}
                            >
                              Select Multiple Medication
                            </Text>
                          </div>
                        </div>

                        <MarMedicationModal
                          data={dedupeByDateAndSlot(
                            appointments?.filter((x) => {
                              const status = getStatus(x, date)?.toUpperCase();
                              const isDateMatch =
                                moment(x?.date).utc().format("YYYY-MM-DD") ===
                                moment(date).format("YYYY-MM-DD");
                              if (!isDateMatch || isPRNMed(x)) return false;
                              return (
                                status !== "DISCONTINUED" && status !== "PRN"
                              );
                            }),
                          )}
                          calendar={calendar}
                          close={closeCheckboxModal}
                          opened={checkboxModalOpened}
                          date={moment(date).format("MMMM DD, YYYY")}
                        />
                        {/* Row-per-medication grid — daily */}
                        <div className={classes.marGridWrapper}>
                          <div
                            className={classes.marGrid}
                            style={{
                              gridTemplateColumns: `${marLabelCollapsed ? "36px" : "260px"} repeat(${TIME_SLOTS.length}, 170px)`,
                            }}
                          >
                            {/* Header row */}
                            <div
                              className={`${classes.marHeaderLabelCell}${marLabelCollapsed ? ` ${classes.marHeaderLabelCellCollapsed}` : ""}`}
                              onClick={
                                marLabelCollapsed
                                  ? () => setMarLabelCollapsed(false)
                                  : undefined
                              }
                            >
                              {marLabelCollapsed ? (
                                <ActionIcon
                                  variant="transparent"
                                  size="sm"
                                  className={classes.collapseToggleBtn}
                                  title="Expand medication names"
                                  onClick={() => setMarLabelCollapsed(false)}
                                >
                                  <FiChevronsRight size={14} />
                                </ActionIcon>
                              ) : (
                                <>
                                  <Text className={classes.marHeaderLabelText}>
                                    Medication / Dose
                                  </Text>
                                  <ActionIcon
                                    variant="transparent"
                                    size="sm"
                                    className={classes.collapseToggleBtn}
                                    title="Collapse medication names"
                                    onClick={() => setMarLabelCollapsed(true)}
                                  >
                                    <FiChevronsLeft size={14} />
                                  </ActionIcon>
                                </>
                              )}
                            </div>
                            {TIME_SLOTS.map((slot) => {
                              const now = moment();
                              const slotStart = moment(slot.startTime, "HH");
                              const slotEnd = moment(slot.endTime, "HH:mm");
                              const isCurrentSlot = now.isBetween(
                                slotStart,
                                slotEnd,
                              );
                              return (
                                <div
                                  key={slot.title}
                                  className={
                                    isCurrentSlot
                                      ? classes.marHeaderColCellToday
                                      : classes.marHeaderColCell
                                  }
                                >
                                  <p
                                    className={
                                      isCurrentSlot
                                        ? classes.marHeaderSlotTitleCurrent
                                        : classes.marHeaderSlotTitle
                                    }
                                  >
                                    {slot.title}
                                  </p>
                                </div>
                              );
                            })}
                            {/* Data rows — grouped by medicationId so the label
                                cell spans all slot rows of the same medicine */}
                            {(() => {
                              const groups = [];
                              const seenMed = new Map();
                              for (const row of dailyMedRows) {
                                const medId = row.medicationId || row._id;
                                if (seenMed.has(medId)) {
                                  groups[seenMed.get(medId)].slots.push(row);
                                } else {
                                  seenMed.set(medId, groups.length);
                                  groups.push({ medId, slots: [row] });
                                }
                              }
                              return groups.flatMap(({ slots }) =>
                                slots.map((medRow) => {
                                  const isPRN = isPRNMed(medRow);
                                  const medName =
                                    firstLetterCap(
                                      medRow?.form3?.medicationName,
                                    ) || "-";
                                  const dosage = medRow?.form3?.dosage
                                    ? `${medRow.form3.dosage}${medRow.form3.unit || ""}`
                                    : null;
                                  const formDisplay = [
                                    medRow?.form3?.ingestionMode,
                                    medRow?.form3?.dosageForm,
                                  ]
                                    .filter(Boolean)
                                    .join(" ");
                                  const allDataForDay =
                                    dailyDataBySlot[TIME_SLOTS[0]?.title]
                                      ?.allData || [];
                                  return (
                                    <React.Fragment
                                      key={`${medRow.medicationId || medRow._id}__${medRow.timeSlot || ""}`}
                                    >
                                      {/* Label cell — one per slot row */}
                                      {(() => {
                                        const isNarcotic =
                                          medRow?.form3?.isNarcotic === "Yes";
                                        // Collect all timeslots for this med across every bucket
                                        const allTimeSlots = isPRN
                                          ? null
                                          : (() => {
                                              const medId =
                                                medRow.medicationId ||
                                                medRow._id;
                                              const seen = new Set();
                                              const times = [];
                                              for (const sl of TIME_SLOTS) {
                                                const bucketCards =
                                                  dailyDataBySlot[sl.title]
                                                    ?.cards || [];
                                                for (const c of bucketCards) {
                                                  if (
                                                    (c.medicationId ||
                                                      c._id) === medId &&
                                                    c.timeSlot &&
                                                    !seen.has(c.timeSlot)
                                                  ) {
                                                    seen.add(c.timeSlot);
                                                    times.push(
                                                      moment(
                                                        c.timeSlot,
                                                        "hh:mm A",
                                                      ).format("h:mm A"),
                                                    );
                                                  }
                                                }
                                              }
                                              return times;
                                            })();
                                        const timeSlotDisplay = isPRN
                                          ? "PRN"
                                          : allTimeSlots?.length
                                            ? allTimeSlots.join(", ")
                                            : null;
                                        return (
                                          <div
                                            className={`${classes.marLabelCell}${marLabelCollapsed ? ` ${classes.marLabelCellCollapsed}` : ""}`}
                                            style={{
                                              ...(marLabelCollapsed
                                                ? {
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    gap: 0,
                                                    padding: "4px",
                                                  }
                                                : {}),
                                            }}
                                            onClick={
                                              marLabelCollapsed
                                                ? () =>
                                                    setMarLabelCollapsed(false)
                                                : () =>
                                                    setActiveMedInfo({
                                                      row: medRow,
                                                      slots,
                                                    })
                                            }
                                            title={
                                              marLabelCollapsed
                                                ? medName
                                                : undefined
                                            }
                                          >
                                            {!marLabelCollapsed ? (
                                              <>
                                                <div
                                                  className={
                                                    classes.marLabelTitleRow
                                                  }
                                                >
                                                  <span
                                                    className={`${classes.marLabelPillIconWrap} ${isNarcotic ? classes.marLabelPillIconWrapNarcotic : classes.marLabelPillIconWrapNormal}`}
                                                  >
                                                    <LuPill size={14} />
                                                  </span>
                                                  <p
                                                    className={
                                                      classes.marLabelMedName
                                                    }
                                                  >
                                                    {dosage
                                                      ? `${medName} (${dosage})`
                                                      : medName}
                                                  </p>
                                                  {isNarcotic && (
                                                    <span
                                                      className={
                                                        classes.marNarcoticBadge
                                                      }
                                                    >
                                                      Narcotic
                                                    </span>
                                                  )}
                                                </div>
                                                {formDisplay ? (
                                                  <p
                                                    className={
                                                      classes.marLabelStrength
                                                    }
                                                  >
                                                    {formDisplay}
                                                  </p>
                                                ) : null}
                                                <div
                                                  className={
                                                    classes.marLabelBadgeRow
                                                  }
                                                >
                                                  {medRow?.form3?.frequency ? (
                                                    <p
                                                      className={
                                                        isPRN
                                                          ? classes.marLabelFreqPrn
                                                          : classes.marLabelFreq
                                                      }
                                                    >
                                                      {medRow.form3.frequency}
                                                    </p>
                                                  ) : null}
                                                  {timeSlotDisplay ? (
                                                    <p
                                                      className={
                                                        classes.marLabelTimeSlot
                                                      }
                                                    >
                                                      {timeSlotDisplay}
                                                    </p>
                                                  ) : null}
                                                </div>
                                                {medRow?.form3?.sig ? (
                                                  <p
                                                    className={
                                                      classes.marLabelSig
                                                    }
                                                  >
                                                    {medRow.form3.sig}
                                                  </p>
                                                ) : null}
                                                {medRow?.form3
                                                  ?.medicationInstruction ? (
                                                  <p
                                                    className={
                                                      classes.marLabelMedInfo
                                                    }
                                                  >
                                                    {
                                                      medRow.form3
                                                        .medicationInstruction
                                                    }
                                                  </p>
                                                ) : null}
                                              </>
                                            ) : (
                                              <span
                                                className={`${classes.marLabelPillIconWrap} ${isNarcotic ? classes.marLabelPillIconWrapNarcotic : classes.marLabelPillIconWrapNormal}`}
                                              >
                                                <LuPill size={12} />
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })()}

                                      {/* Status cells per time slot */}
                                      {TIME_SLOTS.map((slot) => {
                                        const card = getDailyCardForMedAndSlot(
                                          medRow,
                                          slot.title,
                                        );
                                        return card ? (
                                          <ScheduleCard
                                            key={`${medRow.medicationId || medRow._id}-${medRow.timeSlot || ""}-${slot.title}`}
                                            calendar={calendar}
                                            date={moment(date).format(
                                              "MM/DD/YYYY",
                                            )}
                                            data={card}
                                            prn={isPRN}
                                            onOpen={
                                              isPRN ? openPRNView : undefined
                                            }
                                            allData={allDataForDay}
                                            scheduledExtras={
                                              card.scheduledExtras
                                            }
                                            showMedDetails={marLabelCollapsed}
                                            timeZone={timeZone}
                                          />
                                        ) : (
                                          <div
                                            key={`${medRow.medicationId || medRow._id}-${slot.title}-empty`}
                                            className={classes.marEmptyCell}
                                          >
                                            —
                                          </div>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                }),
                              );
                            })()}
                            {dailyMedRows.length === 0 && (
                              <div
                                style={{
                                  gridColumn: `1 / -1`,
                                  padding: "40px",
                                  textAlign: "center",
                                }}
                              >
                                <Text color="dimmed" size="sm">
                                  No medications scheduled for this day
                                </Text>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      // MONTHLY
                      <div className={classes.mainContainer}>
                        {/* <Box className={classes.cuationsBox}>
                          <Cuations />
                        </Box> */}
                        <div className={classes.dateAndCarousalRow}>
                          <div className={classes.calendarClientInfo}>
                            {selectedClient && (
                              <>
                                <div className={classes.calendarClientAvatar}>
                                  {decrypt(selectedClient?.imageurl) ? (
                                    <img
                                      src={decrypt(selectedClient?.imageurl)}
                                      alt={selectedClient.name}
                                    />
                                  ) : (
                                    <Text
                                      className={
                                        classes.calendarClientAvatarInitials
                                      }
                                    >
                                      {(selectedClient.name || "")
                                        .split(" ")
                                        .filter(Boolean)
                                        .map((w) => w[0])
                                        .join("")
                                        .toUpperCase()
                                        .slice(0, 2)}
                                    </Text>
                                  )}
                                </div>
                                <div
                                  className={classes.calendarClientNameStack}
                                >
                                  <Text className={classes.calendarClientName}>
                                    {selectedClient.name}
                                  </Text>
                                  {(selectedClient?.id ||
                                    selectedClient?._id) && (
                                    <Text className={classes.calendarClientId}>
                                      {String(
                                        selectedClient?.id ||
                                          selectedClient?._id,
                                      )
                                        .slice(0, 6)
                                        .toLowerCase()}
                                    </Text>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          <div className={classes.calendarNavCenter}>
                            <div className={classes.carouselWrapper}>
                              <ActionIcon
                                variant="default"
                                radius="xl"
                                onClick={() => handleMonthChange("pre")}
                              >
                                <FiChevronLeft size={16} />
                              </ActionIcon>
                              <p className="dateHdng">
                                {moment(month)
                                  .format("MMMM YYYY")
                                  .toUpperCase()}
                              </p>
                              <ActionIcon
                                variant="default"
                                radius="xl"
                                onClick={() => handleMonthChange("next")}
                              >
                                <FiChevronRight size={16} />
                              </ActionIcon>
                            </div>
                          </div>
                          <div className={classes.calendarRightAction}>
                            <Text
                              className={classes.multipleMedBtn}
                              onClick={openCheckboxModal}
                            >
                              Select Multiple Medication
                            </Text>
                          </div>
                        </div>
                        <MarMedicationModal
                          data={dedupeByDateAndSlot(
                            appointments?.filter((x) => {
                              const status = getStatus(
                                x,
                                x.date,
                              )?.toUpperCase();
                              if (isPRNMed(x)) return false;
                              return status !== "DISCONTINUED";
                            }),
                          )}
                          calendar={calendar}
                          close={closeCheckboxModal}
                          opened={checkboxModalOpened}
                          date={datesForMonthView}
                          currentDate={new Date()}
                        />

                        {/* SLIDER + SCHEDULE — column-group structure for pixel-perfect alignment */}
                        <div className={classes.calendarScrollWrapper}>
                          {WEEK_DAYS.map((day, index) => (
                            <div
                              key={day}
                              className={classes.columnWithHeaderMonth}
                              style={{ minWidth: "120px" }}
                            >
                              <div className={classes.sliderItemHeader}>
                                <MonthWeekCard day={day} data={appointments} />
                              </div>
                              <div
                                className={classes.columnBodyMonth}
                                style={{ padding: 0 }}
                              >
                                {datesForMonthView
                                  .filter(
                                    (date) => moment(date).day() === index,
                                  )
                                  ?.map((date, index) => (
                                    <MonthCard
                                      key={index}
                                      date={date}
                                      data={
                                        monthlyDataByDate[
                                          moment(date).format("YYYY-MM-DD")
                                        ] || []
                                      }
                                      calendar={calendar}
                                    />
                                  ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div
              className={
                userId || !show
                  ? classes.scheduleDetailRight100
                  : classes.scheduleDetailRight
              }
            >
              <div className={classes.date}>
                {newCurrentWeek && (
                  <p style={{ textTransform: "uppercase" }}>
                    {getWeekHeading(newCurrentWeek)}
                  </p>
                )}
              </div>
              <MarListTab />
            </div>
          )}
        </div>
        <AddMarReportModal
          opened={opened}
          onclose={close}
          userId={userId}
          selectedClient={selectedClient}
          title={"Create New Report"}
        />
        <PRNMedicationModal
          isNew={isNew}
          ref={prnRef}
          opened={prnShow}
          onclose={prnClose}
          data={selectedClient}
        />
        {activeMedInfo && (
          <MARMedInfoPanel
            row={activeMedInfo.row}
            slots={activeMedInfo.slots}
            onClose={() => setActiveMedInfo(null)}
          />
        )}
      </div>
    </>
  );
};

export default MarTab;
